/**
 * /lesson-maker — оркестратор: форма → план → озвучка → экспорт.
 *
 * Три экрана, и порядок у них не косметический:
 *
 *   1. ЗАКАЗ      тема или свой текст, класс, время, оформление
 *   2. ПЛАН       правка ДО того, как потрачены минуты на озвучку и рендер
 *   3. УРОК       предпросмотр, ЗАМЕРЕННЫЙ хронометраж, экспорты
 *
 * Шаг 2 существует потому, что у Gamma он же — самая хвалимая часть: модель
 * ошибается в структуре чаще, чем в словах, а исправлять структуру после
 * генерации картинок и звука — значит выбрасывать сделанную работу.
 *
 * Черновик пишется в localStorage на каждом шаге. В телеметрии /slideshow-maker
 * восстановление автосохранения предлагалось 3 055 сессиям за 30 дней — то
 * есть каждой третьей: люди закрывают вкладку посреди работы, и это норма,
 * а не сбой.
 */
import './render.js';
import './wiki.js';
import './narrate.js';
import './export-pptx.js';
import './export-more.js';
import './import.js';
/* Свои картинки — Blob'ами в IndexedDB. localStorage сюда не годится
   структурно: там ~5 МБ на весь черновик, а одна фотография с телефона это
   2–5 МБ. Склад общий — им уже пользуются /collage и /zine. */
import { createDraftStore } from '../image-studio/draft.js';

(function () {
    'use strict';

    var CFG = window.__LESSON_CFG || {};
    var T = CFG.i18n || {};
    var LOCALE = window.__LOCALE || 'ru';
    var DRAFT_KEY = 'lesson-maker:draft:v1';

    var $ = function (id) { return document.getElementById(id); };
    var el = function (tag, cls, text) {
        var n = document.createElement(tag);
        if (cls) n.className = cls;
        if (text != null) n.textContent = text;
        return n;
    };
    function t(key, fallback) { return T[key] || fallback || key; }
    /**
     * Единственная дверь, через которую колода попадает в S.deck.
     * Нормализация списочных полей (легаси-`answer` строкой у «порядка» и
     * т.п.) живёт в LessonRender.normalizeDeck — см. комментарий там.
     */
    function adoptDeck(deck) { return window.LessonRender.normalizeDeck(deck); }
    function stage(name) { if (window.__stage) window.__stage(name); }
    function feature(name, data) { if (window.__aeFunnel) window.__aeFunnel(name, data || {}); }

    // ── состояние ────────────────────────────────────────────────────────────
    var S = {
        deck: null,
        brand: null,      // знак школы: {img, url, corner, size, round}
        tracks: null,     // куски озвучки — для пересборки дорожки без пересинтеза
        theme: null,
        themeKey: 'board',
        // ⚠️ Ключ здесь — ИДЕНТИФИКАТОР слайда, а не его номер.
        // Первая версия индексировала по номеру, и после «переставить выше»
        // или «убрать слайд» картинки оставались на прежних местах: на экране
        // чужое фото, и то же самое уезжало в PPTX и в видео. Нумерация —
        // свойство порядка, а привязка картинки — свойство слайда.
        images: {},        // id слайда → HTMLImageElement
        picks: {},         // id слайда → выбранный кандидат (для атрибуции)
        candidates: {},    // id слайда → список кандидатов
        searching: {},     // id слайда → идёт ли поиск прямо сейчас
        audio: null,       // {buffer, starts, durations, total}
        // Куски озвучки по СОДЕРЖИМОМУ (см. LessonNarrate.keyFor). Переживают
        // правку текста, удаление и перестановку слайдов: пересобирается
        // только то, что действительно изменилось.
        voiceCache: {},
        busy: false
    };

    function theme() { return (CFG.themes || {})[S.themeKey] || { bg: '#111', text: '#fff', accent: '#7fd7a8', panel: '#222', muted: '#aaa', on_accent: '#000' }; }

    // ── черновик ─────────────────────────────────────────────────────────────
    function saveDraft() {
        try {
            localStorage.setItem(DRAFT_KEY, JSON.stringify({
                at: Date.now(),
                deck: S.deck,
                // Запись урока в библиотеке: восстановленный черновик правит
                // ЕЁ, а не заводит вторую копию того же урока.
                token: saved.token || null,
                themeKey: S.themeKey,
                picks: S.picks,
                form: readForm()
            }));
        } catch (e) { /* переполненное хранилище не повод ронять урок */ }
    }
    function loadDraft() {
        try { return JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null'); } catch (e) { return null; }
    }

    /*
     * Свои картинки учителя.
     *
     * Текстовый черновик (deck, picks, форма) остаётся в localStorage — он
     * маленький и синхронный. Сюда уезжают ТОЛЬКО файлы: они не помещаются в
     * localStorage и не должны раздувать каждое сохранение по нажатию клавиши.
     *
     * ⚠️ Держим сами файлы, а не отрисованные картинки: восстановление тогда
     * идёт тем же путём, что и первый выбор, и HEIC с поворотом по EXIF ведут
     * себя одинаково до и после перезагрузки.
     */
    var ownStore = null;
    try { ownStore = createDraftStore('lesson-maker'); } catch (e) { ownStore = null; }
    var ownFiles = Object.create(null);      // ключ слайда → File

    function saveOwnImages() {
        if (!ownStore) return;
        var keys = Object.keys(ownFiles);
        try {
            if (!keys.length) { ownStore.clear(); return; }
            ownStore.save(keys.map(function (k) { return ownFiles[k]; }), { keys: keys });
        } catch (e) { /* черновик — удобство, не архив */ }
    }

    /** Новая колода — свои картинки прежней к ней не относятся. */
    function dropOwnImages() {
        ownFiles = Object.create(null);
        if (ownStore) { try { ownStore.clear(); } catch (e) {} }
    }

    /** Вернуть свои картинки в S.images после перезагрузки страницы. */
    function restoreOwnImages() {
        if (!ownStore) return Promise.resolve();
        return Promise.resolve(ownStore.load()).then(function (rec) {
            if (!rec || !rec.files || !rec.files.length) return;
            var keys = (rec.project && rec.project.keys) || [];
            var jobs = rec.files.map(function (file, i) {
                var k = keys[i];
                if (!k || !file) return null;
                ownFiles[k] = file;
                return new Promise(function (resolve) {
                    var img = new Image();
                    img.onload = function () { S.images[k] = img; resolve(); };
                    img.onerror = function () { resolve(); };
                    img.src = URL.createObjectURL(file);
                });
            }).filter(Boolean);
            return Promise.all(jobs);
        }).catch(function () { /* нет склада — просто нет восстановления */ });
    }
    function dropDraft() { try { localStorage.removeItem(DRAFT_KEY); } catch (e) {} }

    // ── форма ────────────────────────────────────────────────────────────────
    function readForm() {
        var mode = document.querySelector('input[name="lm-mode"]:checked');
        return {
            mode: mode ? mode.value : 'topic',
            topic: ($('lm-topic') || {}).value || '',
            text: ($('lm-text') || {}).value || '',
            subject: ($('lm-subject') || {}).value || '',
            grade: ($('lm-grade') || {}).value || '5',
            duration_s: parseInt(($('lm-duration') || {}).value || '300', 10),
            speed: (document.querySelector('.lm-speed.is-on') || {}).dataset
                ? document.querySelector('.lm-speed.is-on').dataset.speed : 'normal',
            voice: ($('lm-voice') || {}).value || CFG.voice_default || '',
            wantVoice: !!($('lm-want-voice') || {}).checked,
            goalMode: (document.querySelector('.lm-goalmode.is-on') || {}).dataset
                ? document.querySelector('.lm-goalmode.is-on').dataset.goal : 'auto',
            ownGoal: (($('lm-own-goal') || {}).value || '').trim(),
            // Задачи — по одной на строку: так их и пишут в конспекте.
            ownTasks: String(($('lm-own-tasks') || {}).value || '').split(String.fromCharCode(10)).map(function (x) { return x.trim(); }).filter(Boolean),
            quiz: !!($('lm-quiz') || {}).checked,
            credits: ['lm-cr-teacher', 'lm-cr-school', 'lm-cr-place']
                .map(function (id) { return (($(id) || {}).value || '').trim(); })
                .filter(Boolean)
        };
    }

    function applyForm(f) {
        if (!f) return;
        var r = document.querySelector('input[name="lm-mode"][value="' + f.mode + '"]');
        if (r) { r.checked = true; syncMode(); }
        ['topic', 'text', 'subject', 'grade', 'duration'].forEach(function (k) {
            var node = $('lm-' + k);
            var v = k === 'duration' ? f.duration_s : f[k];
            if (node && v != null && v !== '') node.value = v;
        });
        if ($('lm-quiz')) $('lm-quiz').checked = f.quiz !== false;
        if (f.speed) selectSpeed(f.speed);
    }

    function syncMode() {
        var mode = readForm().mode;
        $('lm-field-topic').hidden = mode !== 'topic';
        $('lm-field-text').hidden = mode !== 'text';
        if ($('lm-field-pdf')) $('lm-field-pdf').hidden = mode !== 'pdf';
        if ($('lm-field-manual')) $('lm-field-manual').hidden = mode !== 'manual';
        // Заказанное время имеет смысл ТОЛЬКО когда текст пишем мы. Для своего
        // текста учителя длительность не заказывают, а получают — обещать
        // обратное значит либо резать его слова, либо лить воду.
        $('lm-field-duration').hidden = mode !== 'topic';
        $('lm-duration-note').hidden = mode === 'topic';
        // Цель и задачи пишет модель — в ручном режиме их спрашивать не у кого,
        // учитель просто добавит слайд «Цель урока», если он ему нужен.
        if ($('lm-field-goal')) $('lm-field-goal').hidden = mode !== 'topic';
    }

    function selectSpeed(key) {
        Array.prototype.forEach.call(document.querySelectorAll('.lm-speed'), function (b) {
            var on = b.dataset.speed === key;
            b.classList.toggle('is-on', on);
            b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
    }

    // ── смета ────────────────────────────────────────────────────────────────
    // Цену считает СЕРВЕР (LessonPricing) и присылает готовыми числами: длина
    // урока и голос меняют её, а посчитанная в двух местах цена расходится
    // молча. Поэтому запрос повторяется на каждую правку настроек, а не
    // кэшируется один раз при открытии, как было, пока цена была плоской.
    var voicePrice = null;
    var priceReq = 0;

    /**
     * Токены в деньгах читателя. Считает ОБЩИЙ примитив window.__tokenPrice
     * (public/js/token-price.js): курс приезжает из /api/geo-currency, который
     * no-store, — запекать цену в HTML нельзя, страницы тула кэшируются на CF.
     *
     * ⚠️ Отдельная функция, а не вызов по месту: пока курс не приехал, примитив
     * честно отдаёт пустую строку, и КАЖДОЕ место обязано в этом случае
     * промолчать, а не напечатать « · ». Одна точка — одно правило.
     *
     * Зачем вообще: до 2026-08-20 страница показывала «Итого ≈ 4913» и ни
     * одного знака валюты. Замер воронки: 44 сессии увидели цену, 8 нажали
     * «создать». Учитель не обязан знать, сколько стоит токен.
     */
    function money(tokens) {
        var m = window.__tokenPrice && window.__tokenPrice.money(tokens);
        return m ? ' · ' + m : '';
    }

    // Курс приходит отдельным запросом и обычно ПОЗЖЕ первой сметы. Без этой
    // перерисовки цена так и осталась бы в токенах у всех, кто открыл страницу
    // быстрее ответа /api/geo-currency.
    if (window.__tokenPrice) {
        window.__tokenPrice.ready().then(function () { renderPrice(); });
    }

    function loadVoicePrice() {
        var f = readForm();
        var q = '?grade=' + encodeURIComponent(f.grade)
              + '&duration_s=' + encodeURIComponent(f.duration_s)
              + '&speed=' + encodeURIComponent(f.speed || '');
        var my = ++priceReq;

        return fetch('/api/lesson/voice-price' + q, { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
            .then(function (r) { return r.json(); })
            .then(function (j) {
                if (my !== priceReq) return voicePrice;   // ответ устарел — обогнал следующий
                var firstQuote = !voicePrice;
                voicePrice = j;
                renderPrice();
                // Воронка денег была невидима: мы не знали, сколько человек
                // ВООБЩЕ увидели цену, прежде чем уйти. Шлём один раз за
                // сессию — пересчёты при правке длительности не должны
                // раздувать счётчик показов.
                if (firstQuote) {
                    feature('price_seen', {
                        deck: (j.deck || {}).tokens || 0,
                        voice: (j.voice || {}).tokens || 0,
                        total: j.total || 0,
                        plus: !!j.plus,
                        signed: !!j.signed_in,
                        free: !!(j.deck || {}).free,
                    });
                }
                return j;
            })
            .catch(function () { return null; });
    }

    /**
     * Этот вход обращается к модели (и значит, платен)?
     *
     * Сервер сметы про режим не знает — он считает цену читателя, а не заказа.
     * Поэтому режим спрашиваем у формы: `topic` и `text` уходят в
     * /api/lesson/plan и списываются, `pdf` и `manual` не уходят никуда.
     */
    function modelLane() {
        var mode = readForm().mode;
        return mode === 'topic' || mode === 'text';
    }

    function renderPrice() {
        var j = voicePrice;
        if (!j) return;
        var el0 = $('lm-voice-price'), note = $('lm-voice-note'), total = $('lm-price-total');
        var voice = j.voice || {}, deck = j.deck || {};

        // Суточный счётчик — про БЕСПЛАТНЫЙ лейн. Когда урок платный, он
        // говорит «осталось 3 из 3» рядом с ценой в токенах, и человек читает
        // два взаимоисключающих обещания сразу (поймано QA 2026-08-17).
        // ...а на входах, которые в модель не ходят вовсе, он говорит про
        // лейн, которым здесь никто не пользуется.
        var limitBlock = document.querySelector('.lm-limit');
        if (limitBlock) limitBlock.hidden = !modelLane() || !!(deck.enabled && !deck.free);

        if (el0) {
            if (!voice.enabled) {
                el0.textContent = '';
            } else if (voice.free) {
                el0.textContent = t('voice_free_plus', 'Входит в Премиум');
                el0.classList.add('is-free');
            } else {
                el0.classList.remove('is-free');
                el0.textContent = (t('voice_cost', '≈ :n токенов') || '').replace(':n', voice.tokens)
                    + money(voice.tokens);
            }
        }
        if (note) {
            note.textContent = voice.free || !voice.enabled
                ? ''
                : (j.signed_in
                    ? (t('voice_balance', 'На балансе: :n') || '').replace(':n', j.balance)
                        + ' · ' + t('voice_plus_hint', 'в Премиуме озвучка без токенов')
                    : t('voice_signin', 'Нужен вход. В Премиуме озвучка без токенов.'));
        }

        // Итог показываем ВСЕГДА, когда за урок берутся токены: строчка про
        // голос внутри аккордеона, а платит человек за весь результат.
        if (total) {
            var want = !!($('lm-want-voice') || {}).checked;
            var sum = (deck.tokens || 0) + (want ? (voice.tokens || 0) : 0);
            // Свои слайды (PDF или собранные руками) модель не трогает — за
            // колоду денег нет. Это НЕ то же самое, что `deck.free`: там перк
            // Премиума, и в режиме «тема» ему пишет урок модель.
            if (!modelLane()) {
                total.hidden = false;
                total.textContent = (want && !voice.free && voice.enabled)
                    ? (t('price_voice_only', 'Озвучка — :n токенов') || '')
                        .replace(':n', voice.tokens || 0) + money(voice.tokens || 0)
                    : t('price_free_lane', 'Свои слайды — без токенов. Платная здесь только озвучка.');
            } else if (!deck.enabled && !voice.enabled) {
                total.hidden = true;
            } else if (deck.free && (!want || voice.free)) {
                total.hidden = false;
                total.textContent = deck.free_left > 0
                    ? (t('price_plus_left', 'Входит в Премиум — сегодня ещё :n без токенов') || '').replace(':n', deck.free_left)
                    : t('price_plus', 'Входит в Премиум');
            } else {
                total.hidden = false;
                total.textContent = (t('price_total', 'Урок — :deck токенов:voice. Итого ≈ :sum') || '')
                    .replace(':deck', deck.tokens)
                    .replace(':voice', want && !voice.free
                        ? (t('price_plus_voice', ', озвучка ≈ :n') || '').replace(':n', voice.tokens)
                        : '')
                    .replace(':sum', sum) + money(sum);
                // Премиум, истративший дневной урок, иначе видит цену в токенах
                // без единого слова почему — и читает это как «подписка не
                // работает» (поймано QA 2026-08-17).
                if (j.plus && deck.free_left === 0) {
                    total.textContent += ' · ' + t('price_plus_used', 'дневной урок Премиума на сегодня использован');
                }
                if (j.signed_in && typeof j.balance === 'number') {
                    total.textContent += ' · ' + (t('voice_balance', 'На балансе: :n') || '').replace(':n', j.balance);
                }
            }
        }
    }

    // ── шаг 1 → план ─────────────────────────────────────────────────────────
    function buildPlan() {
        if (S.busy) return;
        var f = readForm();
        // Свои слайды собираются не здесь: колода появляется в момент выбора
        // файла. Кнопка «Составить урок» в этом режиме должна сказать это, а
        // не молча ничего не сделать.
        if (f.mode === 'pdf') {
            if (!S.deck) window.__toast(t('need_pdf', 'Выберите файл PDF со своими слайдами'), 'warning');
            else { renderPlan(); show('plan'); }
            return;
        }
        // Ручная сборка: ни модели, ни кассы, ни счётчика — учитель делает
        // слайды сам, мы даём раскладки, картинки и экспорт. Колода создаётся
        // здесь же, поэтому экран плана открывается мгновенно.
        if (f.mode === 'manual') {
            if (!S.deck || (S.deck.meta || {}).source !== 'manual') startManualDeck(f);
            renderPlan();
            show('plan');
            feature('manual_start', { slides: (S.deck.slides || []).length });
            return;
        }
        if (f.mode === 'topic' && !f.topic.trim()) {
            window.__toast(t('need_topic', 'Введите тему урока'), 'warning');
            $('lm-topic').focus();
            return;
        }
        if (f.mode === 'text' && f.text.trim().length < 40) {
            window.__toast(t('need_text', 'Вставьте текст урока — хотя бы несколько предложений'), 'warning');
            $('lm-text').focus();
            return;
        }

        // С 09.10.2026 суточного капа у тула нет вовсе (работа за токены, Фарид:
        // «вообще лимита не должно быть») — cap() отвечает «без потолка», и
        // begin() здесь лишь отмечает прогон в журнале (__runMark). Бесплатную
        // колоду Премиума считает сервер (server_runs, LessonPricing::deckFreeFor).
        var deckPaid = !!(voicePrice && voicePrice.deck && voicePrice.deck.enabled && !voicePrice.deck.free);
        var run = null;
        if (f.mode === 'topic' && !deckPaid && window.__toolLimit) {
            run = window.__toolLimit.begin('lesson-maker');
            if (!run) return;      // блок «на сегодня всё» показан примитивом
        }

        // Потолок задач — тот же, что у сервера (config lesson-maker.goal):
        // сказать его ДО отправки и словами страницы, а не английским отказом.
        var maxTasks = (CFG.goal && CFG.goal.max_tasks) || 6;
        if (f.goalMode === 'own' && f.ownTasks.length > maxTasks) {
            window.__toast((t('too_many_tasks', 'Задач не больше :n — оставьте главные') || '').replace(':n', maxTasks), 'warning');
            if ($('lm-own-tasks')) $('lm-own-tasks').focus();
            return;
        }

        var rid = newRid();
        var handedOff = false;     // ожиданием заказа дальше владеет waitForOrder
        rememberOrder(rid);
        setBusy(true, t('planning', 'Составляем урок…'));
        stage('plan_request');
        feature('plan_request', { mode: f.mode, grade: f.grade, dur: f.duration_s });

        fetch('/api/lesson/plan', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-CSRF-TOKEN': (document.querySelector('meta[name="csrf-token"]') || {}).content || ''
            },
            body: JSON.stringify({
                mode: f.mode, topic: f.topic, text: f.text, subject: f.subject,
                grade: f.grade, duration_s: f.duration_s, speed: f.speed,
                quiz: f.quiz, locale: LOCALE,
                goal_mode: f.goalMode, own_goal: f.ownGoal, own_tasks: f.ownTasks,
                credits: f.credits,
                rid: rid
            })
        }).catch(function (err) {
            // Сеть отказала до ответа (обрыв, смена сети, уход со страницы) —
            // это и только это «обрыв»; ошибка отрисовки ниже им не считается.
            err.transport = true;
            throw err;
        }).then(function (r) {
            return r.json().catch(function () {
                // Не JSON — ответил не наш контроллер, а что-то по дороге
                // (край, прокси, страница ошибки). Судьба заказа неизвестна.
                var e = new Error('plan_transport'); e.transport = true; e.status = r.status;
                throw e;
            }).then(function (j) {
                if (r.status === 202) return { pending: true };
                if (!r.ok) {
                    var e = new Error(j.message || j.error || 'plan_failed');
                    e.status = r.status; e.payload = j;
                    // Ответ контроллера — заказ решён (провал, отказ кассы);
                    // 502/503/504 с JSON бывают и у края, их считаем обрывом.
                    e.transport = !j.error && r.status >= 500;
                    throw e;
                }
                return j;
            });
        }).then(function (j) {
            if (j.pending) { handedOff = true; return waitForOrder(rid, run); }
            // Списываем только за ВЫДАННЫЙ план — провал не стоит суточной
            // единицы, ровно как резерв на сервере.
            if (run) { run.commit(); run = null; }
            planDelivered(j, rid, 'response');
        }).catch(function (e) {
            if (e && e.transport) {
                // Ответ не дошёл: обрыв связи, смена сети, перезагрузка, край.
                // Урок при этом строится и списывается на сервере — до 15.10
                // страница говорила «не удалось», а человек нажимал снова и
                // платил второй раз (возврат №46). Теперь спрашиваем заказ.
                feature('plan_lost_response', { status: (e && e.status) || 0 });
                handedOff = true;
                return waitForOrder(rid, run);
            }
            forgetOrder(rid);
            planRefused(e, run);
        }).finally(function () {
            if (handedOff) return;
            setBusy(false);
            loadVoicePrice();     // баланс и остаток перка сдвинулись
        });
    }

    // ── заказ урока ──────────────────────────────────────────────────────────
    /*
     * Одно нажатие «Составить урок» — один заказ с ключом `rid`
     * (App\Services\Lesson\LessonOrder). Зачем — возврат №46 (14.10.2026):
     * урок на 15 минут строится 60–70 с, у покупателя на 67-й секунде сменилась
     * сеть, и браузер получил «status 0», а сервер достроил урок, списал 2850
     * токенов и положить его было некуда. Он нажал ещё раз (второе списание),
     * перезагрузил страницу — и ушёл с «ничего не сгенерировано».
     *
     * Теперь урок ложится в «Мои генерации» ДО списания, а страница помнит
     * незаконченный заказ (localStorage, общий для вкладок) и при любом обрыве
     * или перезагрузке спрашивает сервер «как там мой урок» вместо того, чтобы
     * объявить провал и предложить заплатить снова.
     */
    var ORDER_KEY = 'lesson-maker:order:v1';
    // Дольше этого заказ «строится» только у умершего процесса (сервер сам
    // перестаёт считать такой заказ живым через 330 с).
    var ORDER_MAX_AGE_MS = 10 * 60 * 1000;

    function newRid() {
        try {
            if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID().replace(/-/g, '');
        } catch (e) { /* старый браузер — ниже */ }
        var s = '';
        while (s.length < 24) s += Math.random().toString(36).slice(2);
        return s.slice(0, 24);
    }
    function rememberOrder(rid) {
        try { localStorage.setItem(ORDER_KEY, JSON.stringify({ rid: rid, at: Date.now() })); } catch (e) { /* без памяти — без ожидания после перезагрузки */ }
    }
    function pendingOrder() {
        try {
            var o = JSON.parse(localStorage.getItem(ORDER_KEY) || 'null');
            if (o && o.rid && Date.now() - (o.at || 0) < ORDER_MAX_AGE_MS) return o;
        } catch (e) { /* испорченная запись — как нет записи */ }
        return null;
    }
    function forgetOrder(rid) {
        try {
            var o = JSON.parse(localStorage.getItem(ORDER_KEY) || 'null');
            if (!o || !rid || o.rid === rid) localStorage.removeItem(ORDER_KEY);
        } catch (e) { /* нечего забывать */ }
    }

    /**
     * Ждать уже оплачиваемый заказ: спрашивать сервер, пока урок не готов.
     * Ответ «не знаю такого» (404) значит, что заказ не состоялся и токены
     * не списаны (сервер пишет урок в библиотеку раньше, чем берёт деньги),
     * — только тогда говорим «не получилось». Обрыв опроса — не ответ:
     * спрашиваем снова, пока не выйдет срок.
     */
    function waitForOrder(rid, run, quiet) {
        setBusy(true, t('plan_still_building', 'Связь прервалась, но урок продолжает собираться — ждём его…'));
        var started = Date.now();
        var tries = 0;
        return new Promise(function (resolve) {
            function finish() { setBusy(false); loadVoicePrice(); resolve(); }
            function lost(why) {
                forgetOrder(rid);
                if (run) { run.abort(); run = null; }
                feature('plan_order_lost', { why: why, tries: tries, resumed: quiet ? 1 : 0 });
                // Вышел из аккаунта — заказ этой вкладке больше не принадлежит;
                // молча, без «не удалось» на свежей странице.
                if (why !== 'signed_out') {
                    window.__toast(t('plan_failed', 'Не удалось составить урок. Попробуйте ещё раз или упростите тему.'), 'error');
                }
                if (window.__aeDiagnostic) window.__aeDiagnostic({ issue: 'plan_failed', status: 0, why: why });
                finish();
            }
            function ask() {
                tries++;
                fetch('/api/lesson/plan/' + encodeURIComponent(rid), {
                    credentials: 'same-origin', headers: { 'Accept': 'application/json' }
                }).then(function (r) {
                    if (r.status === 404) return lost('unknown');
                    if (r.status === 401) return lost('signed_out');
                    if (r.status === 200) {
                        return r.json().then(function (j) {
                            if (run) { run.commit(); run = null; }
                            try {
                                planDelivered(j, rid, 'recovered');
                                feature('plan_recovered', { tries: tries, wait_s: Math.round((Date.now() - started) / 1000) });
                            } catch (err) {
                                // Урок получен и лежит в библиотеке — не повторять
                                // опрос из-за поломки отрисовки, а назвать её.
                                forgetOrder(rid);
                                if (window.__handleError) window.__handleError(err, 'lesson_plan_render');
                            }
                            finish();
                        });
                    }
                    again();     // 202 — строится; 429/5xx — спросим ещё
                }).catch(again);
            }
            function again() {
                if (Date.now() - started > ORDER_MAX_AGE_MS) return lost('timeout');
                var wait = tries < 3 ? 2000 : 4000;
                if (window.__visibleTimeout) window.__visibleTimeout(ask, wait); else setTimeout(ask, wait);
            }
            ask();
        });
    }

    /** Колода выдана: ответом на заказ или по вопросу «как там мой урок». */
    function planDelivered(j, rid, via) {
        forgetOrder(rid);
        freshDeck();
        S.deck = adoptDeck(j.deck);
        // Урок УЖЕ лежит в «Моих генерациях» — сервер положил его туда до
        // списания. Страница берёт его токен себе: следующие правки обновляют
        // эту же запись, а не плодят вторую и не перезаписывают прошлый урок.
        if (j.token) {
            saved.token = j.token; saved.url = j.url || null; saved.shared = !!j.shared;
            renderShare(j.days);
        }
        saveDraft();
        renderPlan();
        show('plan');
        stage('plan_ready');
        feature('plan_ready', {
            src: j.deck.source, slides: (j.deck.slides || []).length,
            words: (j.deck.meta || {}).words,
            spent: j.spent || 0,      // 0 = дневной перк Премиума (или повтор заказа)
            via: via,
        });
        fetchImages();
        // Тема, кадр и длительность — в ту же запись библиотеки. Ссылку при
        // этом НЕ включаем.
        saveDeck({});
    }

    /** Сервер ответил по существу и урока не будет — сказать почему, словами страницы. */
    function planRefused(e, run) {
        if (run) { run.abort(); run = null; }
        var pay = e.payload || {};
        // Где именно человек упёрся — вопрос, на который до сих пор нечем
        // было ответить: 401 (не вошёл) и 402 (не хватило токенов) — это
        // разные починки, а на дашборде оба выглядели как «не получилось».
        if (e.status === 401 || e.status === 402) {
            feature('paywall', { status: e.status, cost: pay.cost || 0, balance: pay.balance || 0, reason: pay.reason || '' });
        }
        // ⚠️ Слова — только страницы: /api/* сняты с SetLocale, и серверная
        // фраза там английская (учитель 08.10 восемь раз прочитал по-английски
        // отказ валидатора на русской странице).
        var msg = e.status === 429
            ? (pay.error === 'quota'
                ? t('quota', 'На сегодня уроки закончились')
                : t('plan_throttled', 'Слишком много попыток подряд — подождите минуту'))
            : e.status === 401
            ? t('deck_signin', 'Войдите, чтобы собрать урок.')
            : e.status === 422
            ? (pay.error === 'too_many_tasks'
                ? (t('too_many_tasks', 'Задач не больше :n — оставьте главные') || '').replace(':n', pay.max || 6)
                : pay.error === 'tasks_required'
                ? t('need_tasks', 'Если написана цель, добавьте задачи — хотя бы одну на строку')
                : t('plan_invalid', 'Проверьте поля формы — что-то из них сервер не принял'))
            : e.status === 402 && pay.reason === 'held'
            // Токены есть, их держит ваш же урок, который ещё строится
            // (другая вкладка). Сказать «пополните» — просить заплатить дважды.
            ? t('deck_tokens_held', 'Токены заняты уроком, который ещё собирается. Дождитесь его — он появится в «Моих генерациях».')
            : e.status === 402
            ? (t('deck_no_tokens', 'Не хватает токенов: урок стоит :cost, на балансе :n') || '')
                .replace(':cost', (pay.cost || 0) + money(pay.cost || 0))
                .replace(':n', pay.balance || 0)
            : t('plan_failed', 'Не удалось составить урок. Попробуйте ещё раз или упростите тему.');
        window.__toast(msg, 'error');
        if (window.__aeDiagnostic) window.__aeDiagnostic({ issue: 'plan_failed', status: e.status || 0 });
    }

    /**
     * Началась НОВАЯ колода (план, ручная сборка, PDF): у неё своя запись в
     * библиотеке. До 15.10.2026 токен прошлого урока здесь не сбрасывался, и
     * второй урок в той же вкладке тихо перезаписывал первый в «Моих
     * генерациях» — а включённая на первый урок ссылка начинала показывать
     * второй.
     */
    function freshDeck() {
        saved.token = null; saved.url = null; saved.shared = false;
        saved.shareCredits = false; saved.hasCredits = false;
        renderShare();
        S.images = {}; S.picks = {}; S.candidates = {}; S.searching = {}; dropOwnImages();
        S.audio = null; S.tracks = null; S.voiceCache = {};
    }

    // ── картинки ─────────────────────────────────────────────────────────────
    function fetchImages() {
        var slides = (S.deck && S.deck.slides) || [];
        var topic = (S.deck.meta && S.deck.meta.topic) || S.deck.title || '';
        var queue = [];
        slides.forEach(function (s, i) {
            if (window.LessonRender.needsImage(s.layout)) queue.push(i);
        });

        // Последовательно и без спешки: Wikimedia отвечает 429 на залп с
        // одного адреса (поймано при разведке 2026-08-17).
        var p = Promise.resolve();
        queue.forEach(function (i) {
            p = p.then(function () {
                var s = slides[i];
                var k = keyOf(s, i);
                S.searching[k] = true;
                markSlideImage(i);
                return window.LessonWiki.search(s.image_query || s.title, LOCALE, topic, s.image_query_en)
                    .then(function (list) {
                        S.candidates[k] = list;
                        S.searching[k] = false;
                        if (!list.length) { markSlideImage(i); return; }
                        return pickImage(i, 0, true);
                    })
                    .catch(function () { S.searching[k] = false; markSlideImage(i); });
            });
        });
        return p.then(function () { saveDraft(); });
    }

    /** Найти фотографию для ОДНОГО слайда (после того как его переписали). */
    function fetchImageFor(i) {
        var s = (S.deck.slides || [])[i];
        if (!s) return Promise.resolve();
        var k = keyOf(s, i);
        var topic = (S.deck.meta && S.deck.meta.topic) || S.deck.title || '';
        S.searching[k] = true;
        markSlideImage(i);

        return window.LessonWiki.search(s.image_query || s.title, LOCALE, topic, s.image_query_en)
            .then(function (list) {
                S.candidates[k] = list;
                S.searching[k] = false;
                if (!list.length) { markSlideImage(i); return; }
                return pickImage(i, 0, true);
            })
            .catch(function () { S.searching[k] = false; markSlideImage(i); });
    }

    function pickImage(i, ci, silent) {
        var k = keyOf((S.deck.slides || [])[i], i);
        var cand = (S.candidates[k] || [])[ci];
        if (!cand) return Promise.resolve();
        return window.LessonWiki.load(cand.url).then(function (img) {
            S.images[k] = img;
            S.picks[k] = cand;
            var sl = (S.deck.slides || [])[i];
            if (sl) delete sl.own_image;
            delete ownFiles[k];
            markSlideImage(i);
            if (!silent) saveDraft();
        }).catch(function () {
            // Одна недоступная картинка не должна валить слайд — пробуем
            // следующего кандидата, и только потом сдаёмся.
            if (ci + 1 < (S.candidates[k] || []).length) return pickImage(i, ci + 1, silent);
            markSlideImage(i);
        });
    }

    /*
     * Пустая колода для ручной сборки.
     *
     * Титул кладём сразу: пустой экран со словами «добавьте слайд» — это
     * инструкция, а первый слайд с полем «Заголовок» — уже работа. Название
     * берём из поля темы, если учитель его заполнил, прежде чем передумал.
     */
    function startManualDeck(f) {
        var title = (f.topic || '').trim() || t('manual_deck', 'Мой урок');
        freshDeck();
        S.deck = adoptDeck({
            title: title,
            objectives: [],
            homework: '',
            slides: [{ id: 'm1', layout: 'title', title: title, subtitle: '', narration: '', image_query: '', bullets_src: 'manual' }],
            meta: {
                topic: title, subject: f.subject, grade: f.grade,
                source: 'manual', words: 0, est_duration_s: 0
            }
        });
        saveDraft();
    }

    /**
     * Колода собрана человеком, а не моделью?
     *
     * От этого зависит ровно одно: показывать ли кнопки ⟳ − + (они зовут
     * модель). Право на её вызовы начисляется вместе с ВЫДАННОЙ колодой
     * (App\Services\Lesson\LessonRework), а ручная и PDF-колоды его не дают —
     * рисовать кнопку, которая ответит «переделки закончились», значит обещать
     * несуществующее.
     */
    function isHandmade() {
        var src = (S.deck && S.deck.meta && S.deck.meta.source) || '';
        return src === 'manual' || src === 'pdf';
    }

    /** Добавить слайд выбранной раскладки в конец. */
    function addSlide(layout) {
        if (!S.deck) return;
        var reg = (CFG.layouts || {})[layout];
        if (!reg) return;
        var slide = { id: 'm' + (S.deck.slides.length + 1) + '-' + layout, layout: layout,
                      narration: '', image_query: '', bullets_src: 'manual' };
        // Поля заводим ПУСТЫМИ, но нужного типа: редактор строит форму по
        // реестру, а рисовалка ждёт массив там, где объявлен список.
        (reg.fields || []).forEach(function (name) {
            if (slide[name] !== undefined) return;
            slide[name] = fieldKind(layout, name) === 'list' ? [] : (fieldKind(layout, name) === 'pairs' ? [] : '');
        });
        S.deck.slides.push(slide);
        reindexAfterMutation();
        feature('slide_added', { layout: layout });
    }

    /** Наполнить выпадающий список раскладок (кроме служебной «Источники»). */
    function fillLayoutPicker() {
        var sel = $('lm-add-layout');
        if (!sel) return;
        sel.textContent = '';
        Object.keys(CFG.layouts || {}).forEach(function (key) {
            if (key === 'credits') return;   // слайд источников собирается сам
            var o = document.createElement('option');
            o.value = key;
            o.textContent = t('layout_' + key, key);
            sel.appendChild(o);
        });
    }

    // ── экран «План» ─────────────────────────────────────────────────────────
    function renderPlan() {
        var wrap = $('lm-plan-list');
        wrap.textContent = '';
        var slides = (S.deck && S.deck.slides) || [];

        slides.forEach(function (s, i) {
            var card = el('div', 'lm-card');
            card.dataset.i = String(i);

            var head = el('div', 'lm-card-head');
            head.appendChild(el('span', 'lm-badge', String(i + 1)));
            var lsel = document.createElement('select');
            lsel.className = 'lm-input lm-layoutsel';
            lsel.setAttribute('aria-label', t('slide_layout', 'Раскладка слайда'));
            Object.keys(CFG.layouts || {}).forEach(function (key) {
                if (key === 'credits' && s.layout !== 'credits') return;
                var o = document.createElement('option');
                o.value = key;
                o.textContent = t('layout_' + key, key);
                if (key === s.layout) o.selected = true;
                lsel.appendChild(o);
            });
            lsel.addEventListener('change', function () {
                s.layout = lsel.value;
                // Поля новой раскладки, которых у слайда нет, заводим нужного
                // типа — иначе список, ожидаемый рисовалкой, придёт строкой.
                (((CFG.layouts || {})[s.layout] || {}).fields || []).forEach(function (name) {
                    if (s[name] === undefined) {
                        s[name] = fieldKind(s.layout, name) === 'line' ? '' :
                                  (fieldKind(s.layout, name) === 'text' ? '' : []);
                    }
                });
                saveDraft();
                renderPlan();
            });
            head.appendChild(lsel);

            var del = el('button', 'lm-icon-btn', '×');
            del.type = 'button';
            del.title = t('remove_slide', 'Убрать слайд');
            del.setAttribute('aria-label', t('remove_slide', 'Убрать слайд'));
            del.addEventListener('click', function () { removeSlide(i); });

            var up = el('button', 'lm-icon-btn', '↑');
            up.type = 'button'; up.title = t('move_up', 'Выше');
            up.setAttribute('aria-label', t('move_up', 'Выше'));
            up.addEventListener('click', function () { moveSlide(i, -1); });

            var spacer = el('span', 'lm-spacer');
            head.appendChild(spacer);

            // Правка ОДНОГО слайда, а не всей колоды: «этот не годится»,
            // «слишком сложно для моего класса», «слишком просто». Один вызов
            // модели, и кэш озвучки пересинтезирует ровно эту реплику —
            // остальной урок не трогается.
            // ⚠️ Значки НЕ стрелки: рядом уже стоит «↑» = «поднять слайд
            // выше», и вторая такая же стрелка со смыслом «сложнее» читалась
            // как та же кнопка (поймано скриншотом на телефоне). «−» и «+» —
            // «проще» и «сложнее» — ни с чем не путаются.
            (isHandmade() ? [] :
             [['replace', '⟳', 'reslide_replace'],
              ['simpler', '−', 'reslide_simpler'],
              ['harder',  '+', 'reslide_harder']]).forEach(function (m) {
                var b = el('button', 'lm-icon-btn lm-act', m[1]);
                b.type = 'button';
                b.title = t(m[2], m[0]);
                b.setAttribute('aria-label', t(m[2], m[0]));
                b.addEventListener('click', function () { reslide(i, m[0]); });
                head.appendChild(b);
            });

            head.appendChild(up);
            head.appendChild(del);
            card.appendChild(head);

            slideFields(s).forEach(function (f) {
                card.appendChild(buildField(s, f));
            });

            if (s.layout !== 'title' && s.layout !== 'credits') {
                var nar = fieldArea(s.narration || '', function (v) {
                    s.narration = v;
                    // Обесценивается только СВЁРСТАННАЯ дорожка (тайминги
                    // поехали), но НЕ куски: у неизменённых слайдов ключ тот же,
                    // и на пересборке они не будут синтезироваться заново.
                    S.audio = null;
                    updatePlanMeta();
                    saveDraft();
                }, t('slide_narration', 'Что говорит учитель'), 3);
                nar.classList.add('lm-narration');
                card.appendChild(nar);
            }

            if (window.LessonRender.needsImage(s.layout)) {
                var strip = el('div', 'lm-imgs');
                strip.id = 'lm-imgs-' + i;
                card.appendChild(strip);
                markSlideImage(i, strip);

                // Своё изображение. Учитель часто уже имеет нужную схему или
                // фотографию с урока, и подбор из Википедии ей не замена.
                // Файл никуда не отправляется — он читается в объект URL и
                // рисуется на холсте здесь же.
                var own = el('button', 'lm-own', t('own_image', 'Своё изображение'));
                own.type = 'button';
                own.addEventListener('click', function () { pickOwnImage(i); });
                strip.appendChild(own);

                // Искать самому. Запрос уходит тем же путём, что и
                // автоматический (LessonWiki.search), — включая английский
                // лейн Викисклада: замер 17.08 показал, что родной язык
                // отдаёт ноль картинок на ko и id.
                var q = el('input', 'lm-input lm-imgq');
                q.type = 'search';
                q.placeholder = t('img_search_ph', 'Искать другую картинку');
                q.setAttribute('aria-label', t('img_search_ph', 'Искать другую картинку'));
                q.addEventListener('keydown', function (ev) {
                    if (ev.key === 'Enter') { ev.preventDefault(); searchImageFor(i, q.value); }
                });
                var go = el('button', 'lm-btn lm-btn-ghost lm-btn-sm', t('img_search', 'Найти'));
                go.type = 'button';
                go.addEventListener('click', function () { searchImageFor(i, q.value); });
                strip.appendChild(q);
                strip.appendChild(go);
            }

            wrap.appendChild(card);
        });

        updatePlanMeta();
    }

    /*
     * Какие поля у слайда и как их править.
     *
     * Форма поля выводится из ИМЕНИ, а список полей — из реестра раскладок
     * (config/lesson-maker.php → layouts.*.fields), который рисовалка,
     * экспорт PPTX и конспект уже читают. Один источник на всех: раскладка,
     * добавленная в конфиг, получает редактор без единой правки здесь.
     *
     * ⚠️ `answer` двулик по КОНТРАКТУ: у раскладки `order` это список (правильный
     * порядок), у `quiz`/`answer` — строка. Ровно так его нормализует
     * render.js::normalizeDeck, и разойтись с ним нельзя: список, записанный
     * строкой, молча исчезнет со слайда.
     */
    var LIST_FIELDS = ['bullets', 'items', 'left', 'right', 'options', 'lines'];
    var TEXT_FIELDS = ['definition', 'text'];

    function fieldKind(layout, name) {
        if (name === 'answer') return layout === 'order' ? 'list' : 'text';
        if (name === 'facts') return 'pairs';
        if (LIST_FIELDS.indexOf(name) >= 0) return 'list';
        if (TEXT_FIELDS.indexOf(name) >= 0) return 'text';
        return 'line';
    }

    /** Поля этого слайда по реестру; неизвестная раскладка — хотя бы заголовок. */
    function slideFields(s) {
        var reg = (CFG.layouts || {})[s.layout];
        return (reg && reg.fields && reg.fields.length) ? reg.fields.slice() : ['title'];
    }

    /** Подпись поля. Два имени исторические — ключи для них уже переведены. */
    function fieldLabel(name) {
        if (name === 'title' || name === 'term' || name === 'question') {
            return t('slide_title', 'Заголовок слайда');
        }
        if (name === 'bullets') return t('slide_bullets', 'Тезисы на экране — по одному в строке');
        return t('field_' + name, name);
    }

    function buildField(s, name) {
        var kind = fieldKind(s.layout, name);

        if (kind === 'line') {
            return fieldInput(s[name] || '', function (v) {
                s[name] = v;
                saveDraft();
            }, fieldLabel(name));
        }

        if (kind === 'text') {
            return fieldArea(s[name] || '', function (v) {
                s[name] = v;
                saveDraft();
            }, fieldLabel(name), 3);
        }

        if (kind === 'pairs') {
            // «Числа»: значение и подпись под ним. На экране это одна строка
            // «12 | миллионов лет» — вводить два поля на каждый факт значит
            // построить таблицу там, где хватает строки.
            var pairs = (s[name] || []).map(function (f) {
                return [f.value || '', f.label || ''].join(' | ');
            }).join('\n');
            return fieldArea(pairs, function (v) {
                s[name] = v.split('\n').map(function (line) {
                    var parts = line.split('|');
                    return { value: (parts[0] || '').trim(), label: (parts.slice(1).join('|') || '').trim() };
                }).filter(function (f) { return f.value || f.label; });
                saveDraft();
            }, fieldLabel(name), 3);
        }

        return fieldArea((s[name] || []).join('\n'), function (v) {
            s[name] = v.split('\n').map(function (x) { return x.trim(); }).filter(Boolean);
            saveDraft();
        }, fieldLabel(name), 3);
    }

    function fieldInput(value, onChange, label) {
        var w = el('label', 'lm-field');
        w.appendChild(el('span', 'lm-label', label));
        var i = el('input', 'lm-input');
        i.type = 'text'; i.value = value;
        i.addEventListener('change', function () { onChange(i.value); });
        w.appendChild(i);
        return w;
    }

    function fieldArea(value, onChange, label, rows) {
        var w = el('label', 'lm-field');
        w.appendChild(el('span', 'lm-label', label));
        var a = el('textarea', 'lm-input lm-area');
        a.rows = rows || 3; a.value = value;
        a.addEventListener('change', function () { onChange(a.value); });
        w.appendChild(a);
        return w;
    }

    function markSlideImage(i, strip) {
        strip = strip || $('lm-imgs-' + i);
        if (!strip) return;
        strip.textContent = '';
        var slide = (S.deck.slides || [])[i] || {};
        var k = keyOf(slide, i);
        var list = S.candidates[k] || [];
        // Страница из чужого файла — уже готовая картинка слайда, и говорить
        // про неё «фото не нашлось» бессмысленно: её никто и не искал.
        if (slide.imported) {
            strip.appendChild(el('span', 'lm-imgs-none', t('pdf_page', 'Страница вашего файла')));
            return;
        }
        if (!list.length) {
            // ⚠️ «Не нашлось» и «ещё не искали» — РАЗНЫЕ вещи, и путать их
            // нельзя. Первая версия рисовала «фото не нашлось» сразу при
            // создании полосы, то есть сообщала об исходе поиска, которого
            // ещё не было. Учитель видел приговор и шёл дальше, хотя через
            // пару секунд фотографии появлялись.
            strip.appendChild(el('span', 'lm-imgs-none', S.searching[k]
                ? t('searching_photo', 'Ищем фотографии…')
                : t('no_photo', 'Фото не нашлось — слайд будет текстовым')));
            return;
        }
        list.slice(0, 6).forEach(function (c, ci) {
            var b = el('button', 'lm-thumb');
            b.type = 'button';
            b.style.backgroundImage = 'url("' + c.url + '")';
            b.title = window.LessonWiki.credit(c);
            b.setAttribute('aria-label', c.title);
            if (S.picks[k] && S.picks[k].url === c.url) b.classList.add('is-on');
            b.addEventListener('click', function () {
                pickImage(i, ci).then(function () { markSlideImage(i); });
            });
            strip.appendChild(b);
        });
        if (!S.picks[k] && S.images[k]) {
            strip.appendChild(el('span', 'lm-credit', t('own_image_set', 'Используется ваше изображение')));
        } else if (slide.own_image && S.images[k] && isOwnImageUrl(slide.image)) {
            strip.appendChild(el('span', 'lm-credit', t('own_image_kept', 'Ваша картинка сохранена вместе с уроком')));
        } else if (slide.own_image && !S.images[k]) {
            // Урок открыт заново, а файла нет: он и не сохранялся. Раньше
            // слайд просто оказывался без картинки, и понять, почему, было
            // неоткуда.
            strip.appendChild(el('span', 'lm-credit lm-warn', t('own_image_gone', 'Ваша картинка не хранится в библиотеке — выберите файл заново')));
        }
        var pick = S.picks[k];
        if (pick) {
            var cr = el('span', 'lm-credit', window.LessonWiki.credit(pick));
            strip.appendChild(cr);
        }
    }

    /**
     * Поиск иллюстрации по запросу учителя.
     *
     * Запрос ЗАПОМИНАЕТСЯ в `image_query` слайда: он же уедет в черновик и в
     * сохранённый урок, поэтому повторное открытие ищет то, что просили, а не
     * то, что когда-то придумала модель.
     */
    function searchImageFor(i, query) {
        query = String(query || '').trim();
        if (!query) return;
        var slide = (S.deck.slides || [])[i];
        if (!slide) return;
        var k = keyOf(slide, i);
        slide.image_query = query;
        // Ручной запрос — уже на нужном языке; английский лейн Викисклада
        // подставлять нечем, и выдумывать перевод здесь нельзя.
        slide.image_query_en = '';
        S.searching[k] = true;
        markSlideImage(i);
        feature('img_search', { i: i });
        window.LessonWiki.search(query, LOCALE, '', '')
            .then(function (list) {
                S.searching[k] = false;
                S.candidates[k] = list;
                if (!list.length) {
                    markSlideImage(i);
                    window.__toast(t('img_none_found', 'По этому запросу картинок не нашлось'), 'warning');
                    return;
                }
                return pickImage(i, 0).then(function () { markSlideImage(i); });
            })
            .catch(function () {
                S.searching[k] = false;
                markSlideImage(i);
                window.__toast(t('img_search_failed', 'Не удалось поискать картинки'), 'error');
            })
            .then(function () { saveDraft(); });
    }

    /**
     * Уменьшить картинку до размера слайда.
     *
     * Слайд рисуется в 1920×1080 — всё, что больше, это байты, которые мы
     * хранили бы месяц зря, а учитель грузит с телефона 8–12 мегапикселей.
     * Пере-кодируем в JPEG, кроме PNG: у схем и скриншотов с резкими
     * границами JPEG даёт ореолы вокруг букв, и на проекторе это видно.
     *
     * @returns {Promise<Blob>} исходник, если уменьшать нечего
     */
    function shrinkImage(file, img) {
        var cfg = CFG.own_images || {};
        var maxSide = cfg.max_side || 1920;
        var side = Math.max(img.naturalWidth || img.width, img.naturalHeight || img.height);
        var keepPng = String(file.type || '').indexOf('png') >= 0;

        if (side <= maxSide && file.size <= (cfg.max_bytes || 2097152)) {
            return Promise.resolve(file);
        }

        var k = Math.min(1, maxSide / side);
        var c = document.createElement('canvas');
        c.width = Math.max(1, Math.round((img.naturalWidth || img.width) * k));
        c.height = Math.max(1, Math.round((img.naturalHeight || img.height) * k));
        c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);

        return new Promise(function (resolve) {
            c.toBlob(function (blob) {
                // Не смогли пере-кодировать — отдаём исходник: потолок его
                // всё равно проверит сервер, а отказ без файла хуже большого
                // файла.
                resolve(blob || file);
            }, keepPng ? 'image/png' : 'image/jpeg', cfg.quality || 0.85);
        });
    }

    /**
     * Положить свою картинку рядом с уроком.
     *
     * Тихо и необязательно: пока урок не сохранён (гость, или колода ещё без
     * адреса) картинка живёт в браузере, как и раньше. Отказ загрузки НЕ
     * отменяет выбор — слайд уже нарисован, и терять его из-за сети нельзя.
     */
    function uploadOwnImage(i, file, img) {
        if (!saved.token || !(voicePrice && voicePrice.signed_in)) {
            return Promise.resolve(null);
        }
        var slide = (S.deck.slides || [])[i];
        if (!slide) return Promise.resolve(null);
        var k = keyOf(slide, i);

        return shrinkImage(file, img).then(function (blob) {
            var fd = new FormData();
            fd.append('token', saved.token);
            fd.append('key', k);
            fd.append('file', blob, (file.name || 'image').replace(/[^\w.\-]+/g, '_'));

            return fetch('/api/lesson/image', {
                method: 'POST', credentials: 'same-origin',
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-TOKEN': (document.querySelector('meta[name="csrf-token"]') || {}).content || ''
                },
                body: fd
            }).then(function (r) {
                if (r.ok) return r.json();
                var e = new Error('upload'); e.status = r.status; throw e;
            }).then(function (j) {
                // Адрес уезжает В КОЛОДУ: с этого момента урок, открытый
                // заново или по ссылке, находит картинку сам.
                slide.image = j.url;
                feature('own_image_stored', { kb: Math.round((j.bytes || 0) / 1024) });
                saveDraft();
                saveDeck({});
                return j;
            });
        }).catch(function (e) {
            // Причину берём из строк СТРАНИЦЫ: /api/* сняты с SetLocale, и
            // серверный текст там всегда английский.
            var msg = (e && e.status) === 422
                ? t('own_image_rejected', 'Эту картинку сохранить не удалось — попробуйте другую')
                : t('own_image_not_stored', 'Картинка вставлена, но не сохранена на сервере — в этом уроке она останется только здесь');
            window.__toast(msg, 'warning');
            feature('own_image_store_failed', { status: (e && e.status) || 0 });
            return null;
        });
    }

    /**
     * Подставить собственный файл вместо найденного снимка.
     *
     * Никакой загрузки на сервер: `URL.createObjectURL` + отрисовка на холсте.
     * Поэтому и атрибуция ему не нужна — это файл учителя, а не чужая
     * лицензированная работа, и в слайд источников он не попадает.
     */
    function pickOwnImage(i) {
        var input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.addEventListener('change', function () {
            var file = input.files && input.files[0];
            if (!file) return;
            var url = URL.createObjectURL(file);
            var img = new Image();
            img.onload = function () {
                var k = keyOf((S.deck.slides || [])[i], i);
                S.images[k] = img;
                // Своя картинка — не кандидат Викисклада: снимаем атрибуцию,
                // иначе на слайде источников появится чужое имя под чужим файлом.
                S.picks[k] = null;
                var sl = (S.deck.slides || [])[i];
                if (sl) sl.own_image = true;
                ownFiles[k] = file;
                saveOwnImages();
                markSlideImage(i);
                saveDraft();
                // Кладём рядом с уроком. Не ждём: слайд уже нарисован, а
                // отправка мегабайта не должна держать интерфейс.
                uploadOwnImage(i, file, img).then(function () { markSlideImage(i); });
            };
            img.onerror = function () {
                window.__toast(t('own_image_failed', 'Не удалось прочитать изображение'), 'error');
                URL.revokeObjectURL(url);
            };
            img.src = url;
        });
        input.click();
    }

    function removeSlide(i) {
        S.deck.slides.splice(i, 1);
        reindexAfterMutation();
    }

    function moveSlide(i, dir) {
        var j = i + dir;
        if (j < 0 || j >= S.deck.slides.length) return;
        var tmp = S.deck.slides[i];
        S.deck.slides[i] = S.deck.slides[j];
        S.deck.slides[j] = tmp;
        reindexAfterMutation();
    }

    /**
     * После перестановки/удаления индексы картинок обязаны переехать вместе со
     * слайдами. Забыть это — получить чужое фото на слайде, причём в PPTX и
     * видео оно уедет тоже: у нас всё завязано на индекс.
     */
    /**
     * Перестановка и удаление больше НИЧЕГО не переиндексируют: картинки
     * лежат под id слайда, и порядок на них не влияет. Осталось лишь
     * обесценить озвучку — её тайминги считались для прежнего порядка.
     */
    function reindexAfterMutation() {
        S.audio = null;
        renderPlan();
        saveDraft();
    }

    /** Устойчивый ключ слайда. Совпадает с `id` из сервиса; страховка — номер. */
    function keyOf(slide, i) {
        if (slide && slide.id) return String(slide.id);
        return 'i' + i;
    }

    /**
     * Картинки МАССИВОМ в порядке слайдов — то, что нужно рендеру и экспортам.
     * Внутри состояния они лежат по id (порядок меняется), наружу отдаются по
     * позиции. Преобразование живёт в одном месте намеренно: две разные
     * трактовки «images» и есть та ошибка, из-за которой фото уезжало к
     * чужому слайду.
     */
    function imagesInOrder() {
        return (S.deck && S.deck.slides ? S.deck.slides : []).map(function (sl, i) {
            return S.images[keyOf(sl, i)] || null;
        });
    }

    function countWords(deck) {
        return (deck.slides || []).reduce(function (a, s) {
            return a + String(s.narration || '').trim().split(/\s+/).filter(Boolean).length;
        }, 0);
    }

    function updatePlanMeta() {
        if (!S.deck) return;
        var meta = S.deck.meta || {};
        var words = countWords(S.deck);
        var wpm = meta.wpm || 109.7;
        var est = words / wpm * 60 + (S.deck.slides || []).length * ((CFG.timing || {}).slide_gap_s || 0.65);
        $('lm-plan-meta').textContent =
            (S.deck.slides || []).length + ' ' + t('slides_n', 'слайдов')
            + ' · ' + words + ' ' + t('words_n', 'слов')
            + ' · ≈ ' + window.LessonExport.mmss(est);
        var note = $('lm-plan-note');
        if (S.deck.source === 'text') {
            note.textContent = t('text_len_note', 'Длительность получилась из вашего текста.');
            note.hidden = false;
        } else {
            note.hidden = true;
        }
    }

    // ── шаг 2 → урок ─────────────────────────────────────────────────────────

    /**
     * Собрать урок БЕЗ озвучки.
     *
     * Не «запасной путь на случай сбоя», а равноправный выбор: учитель часто
     * берёт слайды, чтобы рассказывать самому. Тогда синтез — лишнее ожидание
     * ради файла, который не нужен, а на слабой сети ещё и лишний отказ.
     * Текст диктора при этом НЕ пропадает: он уезжает в заметки докладчика
     * PowerPoint и в конспект — как раз то, по чему учитель ведёт урок.
     */
    function buildSilent() {
        if (S.busy || !S.deck) return;
        S.audio = null;
        addCreditsSlide();
        show('lesson');
        renderLesson();
        stage('lesson_ready_silent');
        feature('lesson_ready', { voice: 0, slides: S.deck.slides.length });
        saveDraft();
    }

    function buildLesson() {
        if (S.busy || !S.deck) return;
        // Галочка — ЕДИНСТВЕННЫЙ выключатель платного лейна. Без этой проверки
        // главная кнопка списывала бы токены у того, кто озвучку не просил.
        if (!readForm().wantVoice) { buildSilent(); return; }
        setBusy(true, t('voicing', 'Озвучиваем урок…'));
        stage('narrate_start');
        var f = readForm();
        var rate = ((CFG.speeds || {})[f.speed] || {}).rate || 0;
        // Голос ЗАДАЁМ явно. Пустое значение отдавало выбор демону, то есть
        // урок звучал «чем придётся» и менялся бы вместе с его умолчанием.
        var voiceOpts = { locale: LOCALE, rate: rate, voice: f.voice };

        // Разрешение на списание берём ДО работы и рассчитываемся после:
        // провалившаяся озвучка не должна стоить учителю ни токена.
        var settle = function (ok, chars) {
            return fetch('/api/lesson/voice-settle', {
                method: 'POST', credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json', 'Accept': 'application/json',
                    'X-CSRF-TOKEN': (document.querySelector('meta[name=\"csrf-token\"]') || {}).content || ''
                },
                body: JSON.stringify({ ok: !!ok, chars: chars || 0 })
            }).catch(function () {});
        };

        // Платим за символы, которые РЕАЛЬНО пойдут в синтез: то, что уже
        // лежит в кэше по содержимому, повторно не стоит ничего. Резерв берём
        // по этому же числу — иначе правка одного слайда держала бы деньги за
        // весь урок.
        var toSynth = window.LessonNarrate.freshChars(S.deck, voiceOpts, S.voiceCache);

        fetch('/api/lesson/voice-permit', {
            method: 'POST', credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json', 'Accept': 'application/json',
                'X-CSRF-TOKEN': (document.querySelector('meta[name=\"csrf-token\"]') || {}).content || ''
            },
            body: JSON.stringify({ chars: Math.max(1, toSynth) })
        }).then(function (r) {
            return r.json().then(function (j) {
                if (!r.ok) { var e = new Error(j.error || 'permit'); e.status = r.status; e.payload = j; throw e; }
                return j;
            });
        }).then(function () {

        return window.LessonNarrate.narrate(S.deck, voiceOpts,
            function (done, total) {
                setBusy(true, t('voicing', 'Озвучиваем урок…') + ' ' + done + '/' + total);
            }, S.voiceCache)
            .then(function (res) {
                S.voiceCache = res.cache;
                if (res.synthesized === 0) {
                    // Ничего не изменилось — честно сказать, что пересобрали
                    // из готового, а не делать вид, что заново озвучили.
                    window.__toast(t('reused_voice', 'Озвучка не менялась — собрали из готового'), 'info');
                }
                S.tracks = res.tracks;      // нужны панели правки звука
                S.audio = window.LessonNarrate.assemble(res.tracks, {
                    leadIn: edit.leadIn, pauses: edit.pauses,
                    music: edit.music ? { buffer: edit.music.buffer, gain: edit.music.gain } : null
                });
                // Списываем только за ВЫДАННУЮ озвучку. Если ничего не
                // синтезировали (всё взято из кэша) — тоже не берём: учитель
                // не должен платить дважды за один и тот же текст.
                settle(res.synthesized > 0, res.chars || 0);
                addCreditsSlide();
                show('lesson');
                renderLesson();
                stage('lesson_ready');
                feature('lesson_ready', {
                    real: Math.round(S.audio.total),
                    target: (S.deck.meta || {}).duration_s || 0,
                    slides: S.deck.slides.length
                });
                saveDraft();
                // Обновляем библиотечную копию: теперь известны хронометраж и
                // окончательные картинки, а токен тот же — второй записи не
                // появится.
                saveDeck({});
            })
            .catch(function (e) {
                settle(false, 0);
                var msg = e && e.status === 402
                    ? (t('voice_no_tokens', 'Не хватает токенов на озвучку. Слайды и конспект готовы и без неё.'))
                    : e && e.status === 401
                    ? t('voice_signin', 'Нужен вход. В Премиуме озвучка без токенов.')
                    : e && e.status === 429
                    ? t('tts_quota', 'Лимит озвучки на сегодня исчерпан')
                    : t('tts_failed', 'Не удалось озвучить урок. Слайды и презентация уже готовы.');
                window.__toast(msg, 'error');
                if (window.__aeDiagnostic) window.__aeDiagnostic({ issue: 'tts_failed', status: (e && e.status) || 0 });
                // Озвучка — не единственная ценность: без неё урок всё равно
                // экспортируется в PPTX и PDF, поэтому пускаем дальше.
                addCreditsSlide();
                show('lesson');
                renderLesson();
            })
            .finally(function () { setBusy(false); });
        }).catch(function (e) {
            setBusy(false);
            var msg = e && e.status === 402
                ? t('voice_no_tokens', 'Не хватает токенов на озвучку. Слайды и конспект готовы и без неё.')
                : e && e.status === 401
                ? t('voice_signin', 'Нужен вход. В Премиуме озвучка без токенов.')
                : t('tts_failed', 'Не удалось озвучить урок. Слайды и презентация уже готовы.');
            window.__toast(msg, 'warning');
            // Отказ в озвучке не должен отнимать сделанное: собираем без неё.
            buildSilent();
        });
    }

    /**
     * Слайд источников. Не «приятная мелочь»: CC-BY и CC-BY-SA требуют указать
     * автора и лицензию, и без этого учитель, показавший урок, нарушает
     * условия — а отвечать за это будет он, не мы.
     */
    function addCreditsSlide() {
        var used = [];
        Object.keys(S.picks).forEach(function (k) {
            var p = S.picks[k];
            if (p) used.push(window.LessonWiki.credit(p));
        });
        S.deck.slides = S.deck.slides.filter(function (s) { return s.layout !== 'credits'; });
        if (!used.length) return;
        S.deck.slides.push({
            id: 'credits', layout: 'credits',
            title: t('sources', 'Источники изображений'),
            bullets: used.concat([t('sources_note', 'Википедия и Викисклад, лицензии указаны рядом')]),
            narration: '', image_query: '', bullets_src: 'auto'
        });
    }

    // ── экран «Урок» ─────────────────────────────────────────────────────────
    var preview = { idx: 0 };

    /**
     * Файлы, которым НУЖЕН звук, без него не предлагаются: кнопка, которая
     * всегда отвечает «сначала озвучьте», — это не подсказка, а тупик.
     *
     * ⚠️ Вынесено из renderLesson и зовётся ещё и из setBusy: тот гасит и
     * зажигает ВСЕ `.lm-act` разом, поэтому любой следующий экспорт возвращал
     * этим трём кнопкам активный вид, хотя звука по-прежнему нет — оставался
     * серый класс на живой кнопке (поймано QA 2026-08-17).
     */
    function syncVoiceOnlyExports() {
        ['lm-exp-video', 'lm-exp-srt', 'lm-exp-wav'].forEach(function (id) {
            var b = $(id);
            if (!b) return;
            // Видео могло быть выключено проверкой поддержки браузера — её
            // вердикт важнее и перебивать его нельзя.
            if (id === 'lm-exp-video' && b.dataset.unsupported === '1') return;
            b.disabled = !S.audio;
            b.classList.toggle('is-off', !S.audio);
        });
    }

    function renderLesson() {
        drawPreview(0);
        syncPlayer();
        var meta = S.deck.meta || {};
        var target = meta.duration_s || 0;
        var box = $('lm-timing');
        box.textContent = '';

        syncVoiceOnlyExports();

        if (!S.audio) {
            var line0 = el('div', 'lm-timing-line');
            line0.appendChild(el('span', 'lm-muted', t('silent_note', 'Урок собран без озвучки: доступны презентация, конспект и PDF.')));
            var addVoice = el('button', 'lm-btn lm-btn-ghost lm-btn-sm lm-act', t('do_voice', 'Озвучить и собрать'));
            addVoice.type = 'button';
            addVoice.addEventListener('click', function () { buildLesson(); });
            line0.appendChild(addVoice);
            box.appendChild(line0);
        }

        if (S.audio) {
            var v = window.LessonNarrate.verdict(S.audio.total, target, countWords(S.deck));
            var line = el('div', 'lm-timing-line');
            line.appendChild(el('strong', null, window.LessonExport.mmss(S.audio.total)));
            if (target && S.deck.source === 'model') {
                line.appendChild(el('span', 'lm-muted',
                    ' / ' + t('ordered', 'заказано') + ' ' + window.LessonExport.mmss(target)));
                if (!v.ok) {
                    var fix = el('button', 'lm-btn lm-btn-ghost lm-btn-sm',
                        v.diff > 0 ? t('make_longer', 'Дотянуть до заказа') : t('make_shorter', 'Ужать до заказа'));
                    fix.type = 'button';
                    fix.addEventListener('click', function () { refit(v); });
                    line.appendChild(fix);
                }
            }
            box.appendChild(line);
        }
        renderSlideStrip();
    }

    function renderSlideStrip() {
        var strip = $('lm-strip');
        strip.textContent = '';
        (S.deck.slides || []).forEach(function (s, i) {
            var b = el('button', 'lm-strip-btn', String(i + 1));
            b.type = 'button';
            if (i === preview.idx) b.classList.add('is-on');
            b.addEventListener('click', function () {
                // Во время прослушивания номер слайда — это ещё и перемотка:
                // «покажи, как звучит третий» не должно требовать ползунка.
                if (player.on && S.audio && S.audio.starts) playFrom(S.audio.starts[i] || 0);
                else { drawPreview(i); if (S.audio) { player.offset = (S.audio.starts || [])[i] || 0; paintPlayer(); } }
            });
            strip.appendChild(b);
        });
    }

    function drawPreview(i) {
        preview.idx = i;
        var cv = $('lm-preview');
        var w = cv.clientWidth || 880;
        var scale = w / window.LessonRender.W;
        cv.width = Math.round(window.LessonRender.W * scale);
        cv.height = Math.round(window.LessonRender.H * scale);
        var ctx = cv.getContext('2d');
        ctx.clearRect(0, 0, cv.width, cv.height);
        var s = (S.deck.slides || [])[i];
        if (s) window.LessonRender.paint(ctx, s, theme(), S.images[keyOf(s, i)] || null, scale, S.brand);
        var notes = $('lm-notes');
        notes.textContent = (s && s.narration) || '';
        notes.hidden = !(s && s.narration);
        renderSlideStrip();
    }

    // ── плеер урока ──────────────────────────────────────────────────────────
    /*
     * Послушать урок ПРЯМО ЗДЕСЬ. До 2026-08-17 озвученный урок можно было
     * только скачать: чтобы услышать одну фразу, учитель забирал WAV или ждал
     * сборки видео. Слайды при этом листались молча — проверить, совпал ли
     * текст с картинкой, было негде.
     *
     * Звук ведёт СЛАЙДЫ, а не наоборот: положение считается по тем же
     * `starts`/`durations`, что уходят в субтитры и в ролик, поэтому увиденное
     * на странице и полученный файл — это одно и то же.
     */
    var player = { src: null, at: 0, offset: 0, on: false, raf: 0, done: false };

    function playerTotal() { return (S.audio && S.audio.total) || 0; }

    function elapsed() {
        if (!player.on) return player.offset;
        var ctx = window.LessonNarrate.audioCtx();
        return Math.min(playerTotal(), player.offset + (ctx.currentTime - player.at));
    }

    function slideAt(sec) {
        var st = (S.audio && S.audio.starts) || [];
        for (var i = st.length - 1; i >= 0; i--) if (sec >= st[i] - 0.001) return i;
        return 0;
    }

    function stopSource() {
        if (player.src) {
            try { player.src.onended = null; player.src.stop(); } catch (e) { /* уже кончился */ }
            player.src = null;
        }
        if (player.raf) { cancelAnimationFrame(player.raf); player.raf = 0; }
    }

    function paintPlayer() {
        var btn = $('lm-play'), seek = $('lm-seek'), clock = $('lm-clock');
        if (!btn) return;
        var t0 = elapsed(), tot = playerTotal();
        btn.textContent = (player.on ? '⏸ ' : '▶ ') + (player.on ? t('pause', 'Пауза') : t('play', 'Прослушать'));
        btn.setAttribute('aria-label', player.on ? t('pause', 'Пауза') : t('play', 'Прослушать'));
        if (seek && document.activeElement !== seek) seek.value = tot ? Math.round(t0 / tot * 1000) : 0;
        if (clock) clock.textContent = window.LessonExport.mmss(t0) + ' / ' + window.LessonExport.mmss(tot);
    }

    function tick() {
        if (!player.on) return;
        var t0 = elapsed();
        var i = slideAt(t0);
        if (i !== preview.idx) drawPreview(i);
        paintPlayer();
        if (t0 >= playerTotal() - 0.03) { pausePlayer(true); return; }
        player.raf = requestAnimationFrame(tick);
    }

    function playFrom(sec) {
        if (!S.audio || !S.audio.buffer) return;
        var ctx = window.LessonNarrate.audioCtx();
        // ⚠️ iOS: контекст создан во время СИНТЕЗА, то есть вне пользовательского
        // жеста, и остаётся suspended. Без resume() прямо в обработчике клика
        // кнопка «Прослушать» на айфоне не делает ничего и не сообщает почему.
        if (ctx.state === 'suspended' && ctx.resume) ctx.resume();

        stopSource();
        var src = ctx.createBufferSource();
        src.buffer = S.audio.buffer;
        src.connect(ctx.destination);
        src.start(0, Math.max(0, Math.min(sec, Math.max(0, playerTotal() - 0.05))));
        player.src = src;
        player.at = ctx.currentTime;
        player.offset = sec;
        player.on = true;
        drawPreview(slideAt(sec));
        paintPlayer();
        player.raf = requestAnimationFrame(tick);
    }

    function pausePlayer(atEnd) {
        var t0 = elapsed();
        stopSource();
        player.on = false;
        player.offset = atEnd ? 0 : t0;
        if (atEnd && !player.done) {
            player.done = true;
            feature('preview_done', { sec: Math.round(playerTotal()) });
        }
        paintPlayer();
    }

    function syncPlayer() {
        var box = $('lm-player');
        if (!box) return;
        var have = !!(S.audio && S.audio.buffer && playerTotal() > 0);
        box.hidden = !have;
        var sound = $('lm-sound');
        if (sound) { sound.hidden = !have; if (have) fillPauseSlides(); }
        if (!have) { stopSource(); player.on = false; player.offset = 0; }
        paintPlayer();
    }

    // ── свои слайды из PDF ───────────────────────────────────────────────────
    /*
     * Колода из чужого файла. Модель не зовём и денег не берём: мы ничего не
     * генерируем, а раскладываем на слайды то, что учитель принёс. Дальше урок
     * идёт обычным путём — озвучка (по своему тексту), хронометраж, экспорт.
     *
     * ⚠️ Импортированный слайд — КАРТИНКА, и это сказано на экране: обещание
     * «текст остаётся текстом в PPTX» на растровую страницу не распространяется.
     */
    function importPdf(file) {
        if (S.busy) return;
        setBusy(true, t('pdf_reading', 'Читаем PDF…'));
        stage('pdf_import');

        window.LessonImport.fromPdf(file, function (n, total) {
            setBusy(true, (t('pdf_reading', 'Читаем PDF…')) + ' ' + n + '/' + total);
        }).then(function (res) {
            if (!res.slides.length) {
                window.__toast(t('pdf_empty', 'В файле не нашлось страниц'), 'warning');
                return;
            }
            freshDeck();
            S.deck = adoptDeck({
                title: String(file.name || '').replace(/\.pdf$/i, '').slice(0, 120) || t('pdf_deck', 'Мои слайды'),
                objectives: [],
                homework: '',
                slides: res.slides,
                meta: {
                    topic: '', subject: readForm().subject, grade: readForm().grade,
                    source: 'pdf', words: 0, est_duration_s: 0
                }
            });

            // Страницы уже растровые — раскладываем их в S.images напрямую,
            // минуя поиск по Википедии: искать иллюстрацию к собственному
            // слайду учителя незачем.
            var pending = res.slides.map(function (s, i) {
                return window.LessonWiki.load(s.image).then(function (img) {
                    S.images[keyOf(s, i)] = img;
                }).catch(function () { /* останется пустой фон */ });
            });

            Promise.all(pending).then(function () {
                renderPlan();
                show('plan');
                feature('pdf_ready', { pages: res.pages, taken: res.taken });
                if (res.pages > res.taken) {
                    window.__toast((t('pdf_cut', 'Взяли первые :n страниц из :all') || '')
                        .replace(':n', res.taken).replace(':all', res.pages), 'info');
                }
                window.__toast(t('pdf_note', 'Импортированные страницы — картинки: в PPTX они не станут редактируемым текстом'), 'info');
                saveDraft();
                saveDeck({});
            });
        }).catch(function (e) {
            window.__toast(t('pdf_failed', 'Не удалось прочитать PDF'), 'error');
            if (window.__aeDiagnostic) window.__aeDiagnostic({ issue: 'pdf_import_failed', msg: String(e && e.message) });
        }).finally(function () { setBusy(false); });
    }

    // ── правка звуковой дорожки ──────────────────────────────────────────────
    /*
     * Вступление, пауза после слайда и фоновая мелодия — это МИКС уже
     * синтезированных кусков, а не новый синтез: применяется мгновенно, не
     * стоит ни токена и не трогает кэш озвучки. Поэтому панель живёт на экране
     * готового урока и пересобирает дорожку на каждое изменение.
     *
     * ⚠️ Вырезание слов сюда сознательно НЕ попало: речь синтезированная, и
     * убрать фразу правильнее правкой ТЕКСТА на экране плана — это обратимо и
     * не рассинхронизирует субтитры с хронометражем.
     */
    var edit = { leadIn: 0, pauses: {}, music: null };

    function rebuildAudio() {
        if (!S.tracks) return;
        var was = player.on;
        if (was) pausePlayer(false);
        S.audio = window.LessonNarrate.assemble(S.tracks, {
            leadIn: edit.leadIn,
            pauses: edit.pauses,
            music: edit.music ? { buffer: edit.music.buffer, gain: edit.music.gain } : null
        });
        renderLesson();
        var note = $('lm-sound-note');
        if (note) {
            note.textContent = (t('sound_total', 'Длительность урока: :t') || '')
                .replace(':t', window.LessonExport.mmss(S.audio.total));
        }
        saveDraft();
    }

    /** Список слайдов в выпадающем «пауза после…». */
    function fillPauseSlides() {
        var sel = $('lm-pause-slide');
        if (!sel) return;
        var cur = sel.value;
        sel.textContent = '';
        (S.deck && S.deck.slides ? S.deck.slides : []).forEach(function (s, i) {
            var o = document.createElement('option');
            o.value = String(i);
            o.textContent = (i + 1) + '. ' + String(s.title || s.term || s.question || '').slice(0, 34);
            sel.appendChild(o);
        });
        if (cur && sel.querySelector('option[value="' + cur + '"]')) sel.value = cur;
    }

    // ── знак школы ───────────────────────────────────────────────────────────
    /*
     * Герб школы или фотография учителя в углу КАЖДОГО слайда — обычная
     * требуемая формальность («презентация должна быть с логотипом»), из-за
     * которой готовый урок иначе приходится дорабатывать руками в PowerPoint.
     *
     * Картинка ужимается до 512 px ДО хранения: логотип на слайде занимает
     * ~10 % высоты, больше не нужно, а в сохранённую колоду ушёл бы мегабайт
     * base64 на каждый урок.
     */
    var BRAND_MAX = 512;

    function brandFromFile(file) {
        return new Promise(function (resolve, reject) {
            var url = URL.createObjectURL(file);
            var img = new Image();
            img.onload = function () {
                var k = Math.min(1, BRAND_MAX / Math.max(img.naturalWidth, img.naturalHeight));
                var c = document.createElement('canvas');
                c.width = Math.max(1, Math.round(img.naturalWidth * k));
                c.height = Math.max(1, Math.round(img.naturalHeight * k));
                c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
                URL.revokeObjectURL(url);
                // PNG, а не JPEG: у гербов и логотипов бывает прозрачный фон,
                // и белый прямоугольник вокруг него виден на тёмной теме.
                resolve(c.toDataURL('image/png'));
            };
            img.onerror = function () { URL.revokeObjectURL(url); reject(new Error('brand_read')); };
            img.src = url;
        });
    }

    function applyBrand(dataUrl, opts) {
        opts = opts || {};
        var row = document.querySelectorAll('.lm-brand-opt');
        if (!dataUrl) {
            S.brand = null;
            if (S.deck && S.deck.meta) delete S.deck.meta.brand;
            Array.prototype.forEach.call(row, function (el) { el.hidden = true; });
            if (S.deck && !$('lm-screen-lesson').hidden) drawPreview(preview.idx);
            return Promise.resolve();
        }

        return new Promise(function (resolve) {
            var img = new Image();
            img.onload = function () {
                S.brand = {
                    img: img,
                    url: dataUrl,
                    corner: opts.corner || (($('lm-brand-corner') || {}).value) || 'br',
                    size: (opts.size || parseInt(($('lm-brand-size') || {}).value || '11', 10)) / 100,
                    round: opts.round !== undefined ? !!opts.round : !!($('lm-brand-round') || {}).checked
                };
                if (S.deck) {
                    S.deck.meta = S.deck.meta || {};
                    S.deck.meta.brand = { url: dataUrl, corner: S.brand.corner, size: S.brand.size, round: S.brand.round };
                }
                Array.prototype.forEach.call(row, function (el) { el.hidden = false; });
                if (S.deck && !$('lm-screen-lesson').hidden) drawPreview(preview.idx);
                resolve();
            };
            img.onerror = function () { resolve(); };
            img.src = dataUrl;
        });
    }

    // ── образец урока для гостя ──────────────────────────────────────────────
    /*
     * Бесплатных прогонов у тула нет, поэтому единственный честный способ
     * показать результат до оплаты — показать ГОТОВЫЙ урок. Файл-образец
     * сгенерирован этим же инструментом (public/data/lesson-samples), и
     * рисуется тем же render.js: витрина не может «выглядеть лучше», чем
     * настоящая выдача, потому что это она и есть.
     *
     * Загружается ЛЕНИВО, при первом открытии аккордеона: на первом экране
     * 15 КБ json и дюжина фотографий не нужны никому.
     */
    var sampleLoaded = false;

    function loadSample(box) {
        if (sampleLoaded) return;
        sampleLoaded = true;
        var body = $('lm-sample-body');
        var src = box.dataset.src;
        feature('sample_open', {});

        fetch(src, { credentials: 'omit' })
            .then(function (r) { return r.ok ? r.json() : Promise.reject(new Error('sample')); })
            .then(function (deck) {
                body.textContent = '';
                // ⚠️ Витрина рисуется СВЕТЛОЙ. Первое, что сказала о нашем
                // образце учитель начальных классов: «фон тёмный, тревожный;
                // для детей до 7 класса нужны спокойные тона либо яркие».
                var th = (CFG.themes || {}).sunny || (CFG.themes || {}).light || theme();
                (deck.slides || []).forEach(function (s, i) {
                    var row = el('div', 'lm-sample-slide');
                    var cv = document.createElement('canvas');
                    cv.setAttribute('aria-label', s.title || '');
                    row.appendChild(cv);
                    if (s.narration) row.appendChild(el('p', 'lm-sample-say', s.narration));
                    body.appendChild(row);

                    var paint = function (img) {
                        var w = cv.clientWidth || 640;
                        var scale = w / window.LessonRender.W;
                        cv.width = Math.round(window.LessonRender.W * scale);
                        cv.height = Math.round(window.LessonRender.H * scale);
                        var c2 = cv.getContext('2d');
                        c2.clearRect(0, 0, cv.width, cv.height);
                        window.LessonRender.paint(c2, s, th, img || null, scale);
                    };
                    paint(null);
                    if (s.image && window.LessonWiki) {
                        window.LessonWiki.load(s.image).then(paint).catch(function () { /* текстовый слайд */ });
                    }
                });
                var files = el('p', 'lm-sample-files', t('sample_files', 'Так же выгружается: PPTX · PDF · конспект · видеоурок · субтитры'));
                body.appendChild(files);
            })
            .catch(function () {
                body.textContent = t('sample_failed', 'Пример не загрузился — но сам инструмент работает');
                sampleLoaded = false;
            });
    }

    // ── библиотека и ссылка «показать урок» ──────────────────────────────────
    /*
     * Урок оплачен, а жил он до закрытия вкладки: черновик в localStorage
     * привязан к одному браузеру. Поэтому готовая колода уезжает на сервер
     * САМА (тихо, без кнопки) — и сразу получает две двери: «Мои генерации» и
     * ссылку, которой учитель делится с классом.
     *
     * Гостя не трогаем: у него нет ни библиотеки, ни владельца записи.
     */
    var saved = { token: null, url: null, shared: false, shareCredits: false, hasCredits: false, busy: false };

    /** Колода для сохранения: с выбранными картинками, чтобы ссылка их показала. */
    function deckForSave() {
        var d = JSON.parse(JSON.stringify(S.deck || {}));
        (d.slides || []).forEach(function (s, i) {
            var pick = S.picks[keyOf(s, i)];
            // Своё изображение (data:) в json не кладём: оно раздует запись до
            // мегабайтов, а ссылка нужна для урока, а не для хранения фото.
            var url = pick && pick.url ? String(pick.url) : '';
            s.image = url.indexOf('http') === 0 ? url : null;
        });
        return d;
    }

    function saveDeck(opts) {
        opts = opts || {};
        if (saved.busy || !S.deck) return Promise.resolve(null);
        if (!(voicePrice && voicePrice.signed_in)) return Promise.resolve(null);
        saved.busy = true;

        return fetch('/api/lesson/save', {
            method: 'POST', credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json', 'Accept': 'application/json',
                'X-CSRF-TOKEN': (document.querySelector('meta[name="csrf-token"]') || {}).content || ''
            },
            body: JSON.stringify({
                deck: deckForSave(),
                theme: S.themeKey,
                aspect: S.aspect,
                token: saved.token,
                share: opts.share,
                // Ключа нет — сервер значение не трогает. Показ реквизитов
                // включается ТОЛЬКО этой галочкой и никогда попутно.
                share_credits: opts.share_credits,
                // Локаль страницы: /api/* сняты с SetLocale, и без этого
                // русский урок получал ссылку /en/lesson/… (проверено стендом).
                locale: LOCALE,
                duration_s: Math.round((S.audio && S.audio.total) || 0)
            })
        }).then(function (r) { return r.ok ? r.json() : null; })
            .then(function (j) {
                if (j) {
                    saved.token = j.token; saved.url = j.url; saved.shared = !!j.shared;
                    saved.shareCredits = !!j.share_credits; saved.hasCredits = !!j.has_credits;
                    renderShare(j.days);
                }
                return j;
            })
            .catch(function () { return null; })
            .finally(function () { saved.busy = false; });
    }

    /**
     * Переписать один слайд: «дай другой», «проще», «сложнее».
     *
     * Картинку слайда СБРАСЫВАЕМ: у нового текста другой сюжет, и оставленное
     * старое фото — это ровно тот случай, когда на экране одно, а говорится
     * другое. Поиск запускается заново по обновлённому image_query.
     */
    function reslide(i, mode) {
        if (S.busy || !S.deck) return;
        setBusy(true, t('reslide_busy', 'Переписываем слайд…'));
        feature('reslide', { mode: mode, i: i });

        fetch('/api/lesson/reslide', {
            method: 'POST', credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json', 'Accept': 'application/json',
                'X-CSRF-TOKEN': (document.querySelector('meta[name="csrf-token"]') || {}).content || ''
            },
            body: JSON.stringify({
                deck: S.deck, index: i, mode: mode,
                grade: readForm().grade, locale: LOCALE
            })
        }).then(function (r) {
            if (r.ok) return r.json();
            var e = new Error('reslide');
            e.status = r.status;
            return Promise.reject(e);
        })
            .then(function (j) {
                var before = JSON.stringify((S.deck.slides || [])[i] || {});
                S.deck = adoptDeck(j.deck);
                var s = (S.deck.slides || [])[i];
                if (JSON.stringify(s || {}) === before) {
                    window.__toast(t('reslide_same', 'Модель вернула тот же текст — попробуйте ещё раз'), 'warning');
                    return;
                }
                var k = keyOf(s, i);
                delete S.images[k]; delete S.picks[k]; delete S.candidates[k];
                renderPlan();
                if (window.LessonRender.needsImage(s.layout)) fetchImageFor(i);
                saveDraft();
                saveDeck({});
            })
            .catch(function (e) {
                // 401 и 402 — это разные починки, и «не получилось» не говорит
                // ни одной из них. Причину берём из строк СТРАНИЦЫ: маршруты
                // /api/* сняты с SetLocale, и серверный `message` там всегда
                // английский — на том же месте тул уже обжигался ссылкой
                // /en/lesson/… для русского урока.
                var msg = (e && e.status) === 401
                    ? t('rework_signin', 'Войдите, чтобы переписывать слайды.')
                    : (e && e.status) === 402
                    ? t('rework_exhausted', 'Переделки этого урока закончились.')
                    : t('reslide_failed', 'Не получилось переписать слайд');
                window.__toast(msg, (e && e.status) === 402 ? 'warning' : 'error');
                feature('rework_denied', { status: (e && e.status) || 0 });
            })
            .finally(function () { setBusy(false); });
    }

    /** Поднять сохранённый урок из библиотеки в рабочее состояние тула. */
    function openSaved(token) {
        setBusy(true, t('planning', 'Составляем урок…'));
        fetch('/api/lesson/deck/' + encodeURIComponent(token), {
            credentials: 'same-origin', headers: { 'Accept': 'application/json' }
        }).then(function (r) { return r.ok ? r.json() : null; })
            .then(function (j) {
                if (!j || !j.deck) { window.__toast(t('deck_gone', 'Урок не найден — возможно, истёк срок хранения'), 'warning'); return; }
                S.deck = adoptDeck(j.deck);
                S.images = {}; S.picks = {}; S.candidates = {}; S.searching = {}; dropOwnImages();
                S.audio = null; S.voiceCache = {};
                S.themeKey = j.theme || S.themeKey;
                saved.token = j.token; saved.shared = !!j.shared; saved.url = j.url || null;
                saved.shareCredits = !!j.share_credits; saved.hasCredits = !!j.has_credits;
                renderShare();
                applyAspect(j.aspect || S.aspect, false);
                // Картинки в колоде — ссылки; подтягиваем их так же, как после
                // генерации, иначе слайды откроются без фотографий.
                (S.deck.slides || []).forEach(function (s, i) {
                    if (!s.image) return;
                    // ⚠️ Своя картинка НЕ кандидат Викисклада. Записав её в
                    // picks, мы бы вернули ровно тот дефект, от которого
                    // защищается выбор файла: атрибуция под слайдом и чужое
                    // имя на слайде «Источники» под фотографией учителя.
                    if (isOwnImageUrl(s.image)) return;
                    S.picks[keyOf(s, i)] = { url: s.image };
                });
                renderPlan();
                show('plan');
                stage('reopen');
                feature('reopen', { slides: (S.deck.slides || []).length });
                restoreImages();
            })
            .catch(function () { /* молча остаёмся на форме */ })
            .finally(function () { setBusy(false); });
    }

    /** Догрузить картинки сохранённой колоды по их адресам. */
    /**
     * Адрес указывает на НАШУ картинку учителя, а не на Викисклад?
     *
     * Вынесено в функцию, потому что проверка нужна в трёх местах, а три
     * написания одной и той же строки разъезжаются при первой же правке
     * маршрута.
     */
    function isOwnImageUrl(url) {
        return String(url || '').indexOf('/api/lesson/image/') === 0;
    }

    function restoreImages() {
        (S.deck.slides || []).forEach(function (s, i) {
            var k = keyOf(s, i);
            // Своя картинка: адрес лежит прямо в слайде, кандидатов у неё нет.
            // 410 от сервера значит «была и удалена по сроку» — тогда слайд
            // сам расскажет об этом полосой, а не останется молча пустым.
            if (isOwnImageUrl(s.image)) {
                if (!window.LessonWiki) return;
                window.LessonWiki.load(s.image).then(function (img) {
                    S.images[k] = img;
                    if (!$('lm-screen-lesson').hidden || !$('lm-screen-plan').hidden) renderPlan();
                }).catch(function () { markSlideImage(i); });
                return;
            }
            var pick = S.picks[k];
            if (!pick || !pick.url || !window.LessonWiki) return;
            window.LessonWiki.load(pick.url).then(function (img) {
                S.images[k] = img;
                if (!$('lm-screen-lesson').hidden || !$('lm-screen-plan').hidden) renderPlan();
            }).catch(function () { /* слайд останется текстовым */ });
        });
    }

    function copyLink(url) {
        if (!url) return;
        var done = function () { window.__toast(t('share_copied', 'Ссылка скопирована'), 'success'); };
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(url).then(done, function () { showLink(url); });
        } else {
            showLink(url);
        }
    }

    /** Буфер недоступен (старый Safari, http) — показать поле, чтобы скопировать руками. */
    function showLink(url) {
        var link = $('lm-share-link');
        if (!link) return;
        link.hidden = false;
        link.value = url;
        link.focus();
        link.select();
    }

    function renderShare(days) {
        var box = $('lm-share');
        if (!box) return;
        box.hidden = !saved.token;
        var link = $('lm-share-link');
        var note = $('lm-share-note');
        if (link) { link.value = saved.url || ''; link.hidden = !saved.shared; }
        var btn = $('lm-share-btn');
        if (btn) {
            btn.textContent = saved.shared
                ? t('share_copy', 'Скопировать ссылку')
                : t('share_make', 'Получить ссылку на урок');
        }
        // Выключить можно только то, что включено.
        var off = $('lm-share-off');
        if (off) off.hidden = !saved.shared;
        // Галочка про реквизиты нужна лишь тогда, когда реквизиты вообще есть
        // И ссылка включена: до этого показывать нечего и некому.
        var credsRow = $('lm-share-creds-row');
        var credsHint = $('lm-share-creds-hint');
        var credsShow = saved.shared && saved.hasCredits;
        if (credsRow) credsRow.hidden = !credsShow;
        if (credsHint) credsHint.hidden = !credsShow;
        var credsBox = $('lm-share-creds');
        if (credsBox) credsBox.checked = !!saved.shareCredits;
        renderCredVis();
        if (note) {
            note.textContent = days
                ? (t('saved_note', 'Урок сохранён в «Моих генерациях» и хранится :n дней') || '').replace(':n', days)
                : note.textContent;
        }
    }

    /**
     * Значок «кто это видит» под полями реквизитов.
     *
     * Показывает СОСТОЯНИЕ, а не намерение: «видит любой, у кого есть ссылка»
     * зажигается, только когда ссылка включена И показ реквизитов разрешён.
     * Формулировка про ссылку, а не «видят все»: страница закрыта от индекса и
     * живёт по неугадываемому адресу, и обещать чужому больше, чем есть, — то
     * же враньё, что и обещать меньше.
     */
    function renderCredVis() {
        var priv = $('lm-cr-vis-private');
        var pub = $('lm-cr-vis-public');
        if (!priv || !pub) return;
        var open = !!(saved.shared && saved.shareCredits);
        priv.hidden = open;
        pub.hidden = !open;
    }

    /** Применить тему: подсветить чип, перерисовать превью. */
    function applyTheme(key, redraw) {
        if (!key) return;
        S.themeKey = key;
        Array.prototype.forEach.call(document.querySelectorAll('.lm-theme'), function (x) {
            var on = x.dataset.theme === key;
            x.classList.toggle('is-on', on);
            x.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        if (redraw && S.deck && !$('lm-screen-lesson').hidden) drawPreview(preview.idx);
    }

    // ── пропорции кадра ──────────────────────────────────────────────────────
    function applyAspect(key, redraw) {
        var A = (CFG.aspects || {})[key] || (CFG.aspects || {})['16:9'] || { w: 1920, h: 1080 };
        S.aspect = key;
        window.LessonRender.setSize(A.w, A.h);
        Array.prototype.forEach.call(document.querySelectorAll('.lm-aspect'), function (b) {
            var on = b.dataset.aspect === key;
            b.classList.toggle('is-on', on);
            b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        if (redraw && S.deck && !$('lm-screen-lesson').hidden) drawPreview(preview.idx);
    }

    function refit(v) {
        if (S.busy) return;
        setBusy(true, t('refitting', 'Подгоняем под время…'));
        var order = window.LessonNarrate.refitOrder(S.deck, S.audio.durations, v.diff > 0);
        fetch('/api/lesson/refit', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json', 'Accept': 'application/json',
                'X-CSRF-TOKEN': (document.querySelector('meta[name="csrf-token"]') || {}).content || ''
            },
            body: JSON.stringify({
                deck: S.deck, delta_words: v.delta_words, order: order,
                grade: (S.deck.meta || {}).grade || '5', locale: LOCALE
            })
        }).then(function (r) {
            if (r.ok) return r.json();
            var e = new Error('refit');
            e.status = r.status;
            return Promise.reject(e);
        })
            .then(function (j) {
                if (!j.deck) throw new Error('refit');
                S.deck = adoptDeck(j.deck);
                feature('refit', { delta: v.delta_words });
                setBusy(false);
                buildLesson();     // переозвучить — иначе время снова оценка
            })
            .catch(function (e) {
                setBusy(false);
                // Та же дверь, что у reslide, — и причина отказа обязана
                // называться так же. Строки берём из СТРАНИЦЫ: /api/* сняты
                // с SetLocale, серверный `message` там всегда английский.
                var msg = (e && e.status) === 401
                    ? t('rework_signin', 'Войдите, чтобы переписывать слайды.')
                    : (e && e.status) === 402
                    ? t('rework_exhausted', 'Переделки этого урока закончились.')
                    : t('refit_failed', 'Не получилось подогнать. Можно поправить текст вручную.');
                window.__toast(msg, 'warning');
                if (e && e.status) feature('rework_denied', { status: e.status, at: 'refit' });
            });
    }

    // ── экспорт ──────────────────────────────────────────────────────────────
    /**
     * Единственная дверь, через которую файл попадает к учителю, — поэтому
     * именно здесь стоит `tool_complete`.
     *
     * До 2026-08-17 его слал ТОЛЬКО видеоэкспорт, а видео собирает меньшинство:
     * на дашборде тул выглядел как «никто не доходит до конца», хотя PPTX и
     * конспект уезжали исправно. Сигнал «результат ВЫДАН» обязан стоять там,
     * где выдача происходит, а не там, где о ней удобнее было вспомнить.
     */
    var completed = false;
    function save(blob, name) {
        if (window.__downloadFile) window.__downloadFile(blob, name);
        else {
            var a = document.createElement('a');
            a.href = URL.createObjectURL(blob); a.download = name; a.click();
        }
        var kind = (name.split('.').pop() || '').toLowerCase();
        var bytes = (blob && blob.size) || 0;
        if (window.__aeToolComplete) {
            window.__aeToolComplete({
                kind: kind,
                bytes: bytes,
                voiced: !!S.audio,
                slides: ((S.deck && S.deck.slides) || []).length,
                first: !completed,          // первый выданный файл за сессию
            });
        }
        completed = true;
        feature('file_out', { kind: kind, kb: Math.round(bytes / 1024) });
    }

    function baseName() {
        return String((S.deck && S.deck.title) || 'lesson')
            .replace(/[\\/:*?"<>|]+/g, ' ').trim().slice(0, 60) || 'lesson';
    }

    function exportPptx() {
        setBusy(true, t('exporting', 'Собираем файл…'));
        var reveal = !!(($('lm-reveal') || {}).checked);
        window.LessonPptx.build(S.deck, theme(), imagesInOrder(), {
            locale: LOCALE, brand: S.brand, reveal: reveal,
            // Подписи слайдов-реакций тренажёра — из локали, а не из кода.
            i18n: {
                praise: t('praise', 'Молодцы!'),
                retry: t('retry', 'Будьте внимательны!'),
                next: t('next_slide', 'Далее →'),
                back: t('back_slide', '← Назад'),
                praise_note: t('praise_note', 'Слайд появляется, когда ученик выбрал верный вариант.'),
                retry_note: t('retry_note', 'Слайд появляется при неверном выборе: «Назад» возвращает к вопросу.')
            }
        })
            .then(function (b) {
                save(b, baseName() + '.pptx');
                feature('export', { kind: 'pptx', reveal: reveal ? 1 : 0 });
            })
            .catch(function (e) { window.__handleError(e, 'pptx'); })
            .finally(function () { setBusy(false); });
    }

    function exportPdf() {
        setBusy(true, t('exporting', 'Собираем файл…'));
        var per = parseInt(($('lm-perpage') || {}).value || '1', 10) || 1;
        window.LessonExport.pdf(S.deck, theme(), imagesInOrder(), { perPage: per, brand: S.brand })
            .then(function (b) {
                // Имя файла говорит, ЧТО внутри: «урок.pdf» и «урок —
                // раздатка 6.pdf» в папке «Загрузки» ищутся по-разному.
                var name = per > 1
                    ? baseName() + ' — ' + (t('handout_file', 'раздатка') + ' ' + per)
                    : baseName();
                save(b, name + '.pdf');
                feature('export', { kind: 'pdf', per: per });
            })
            .catch(function (e) { window.__handleError(e, 'pdf'); })
            .finally(function () { setBusy(false); });
    }

    /**
     * Подписи типов слайдов для документов — из ТОГО ЖЕ места, что бейдж в
     * списке слайдов. Ключи не перечисляются: любой `layout_*`, появившийся в
     * локали, доезжает и до конспекта с картой. Перечисление списком было бы
     * третьим местом, где заводят раскладку, и оно бы отстало — так шесть
     * подписей уже пролежали мёртвыми, пока бейдж печатал английский слаг.
     */
    function layoutLabels() {
        var out = {}, src = CFG.i18n || {};
        Object.keys(src).forEach(function (k) {
            if (k.indexOf('layout_') === 0) out[k] = src[k];
        });
        return out;
    }

    function exportDoc() {
        var d = S.audio ? S.audio.durations : (S.deck.slides || []).map(function () { return 0; });
        save(window.LessonExport.lessonDoc(S.deck, d, Object.assign(layoutLabels(), {
            goal: t('goal_slide_title', 'Цель урока'),
            tasks: t('tasks_label', 'Задачи'),
            objectives: t('objectives', 'Цели урока'),
            plan: t('plan_h', 'Ход урока'),
            at: t('at', 'Время'), len: t('len', 'Длит.'),
            on_screen: t('on_screen', 'На экране'), says: t('says', 'Учитель говорит'),
            homework: t('homework', 'Домашнее задание'),
            handout: t('handout', 'Раздаточный материал'),
            grade_label: t('grade_label', 'класс '),
            duration_label: t('duration_label', 'хронометраж')
        })), baseName() + '.doc');
        feature('export', { kind: 'doc' });
    }

    /** Технологическая карта — документ «для завуча», а не для класса. */
    function exportMap() {
        var d = S.audio ? S.audio.durations : (S.deck.slides || []).map(function () { return 0; });
        var keys = ['map_title', 'map_topic', 'map_subject', 'map_grade', 'map_type', 'map_type_value', 'map_tasks',
            'map_equipment', 'map_equipment_value', 'map_duration', 'map_goal', 'map_results',
            'map_res_subject', 'map_res_meta', 'map_res_personal', 'map_course', 'map_stage',
            'map_time', 'map_teacher', 'map_students', 'map_uud', 'map_draft_note',
            'stage_org', 'stage_goal', 'stage_new', 'stage_fix', 'stage_reflect',
            'act_listen', 'act_answer', 'act_write', 'act_quiz', 'act_summarize', 'act_task',
            'uud_cognitive', 'uud_regulative', 'uud_communicative', 'uud_personal', 'homework'];
        var i18n = layoutLabels();
        keys.forEach(function (k) { i18n[k] = t(k, ''); });

        save(window.LessonExport.lessonMapDoc(S.deck, d, i18n),
            baseName() + ' — ' + (t('map_file', 'технологическая карта')) + '.doc');
        feature('export', { kind: 'map' });
    }

    function exportSrt() {
        if (!S.audio) { window.__toast(t('need_voice', 'Сначала озвучьте урок'), 'warning'); return; }
        save(window.LessonExport.srt(S.deck, S.audio.starts, S.audio.durations), baseName() + '.srt');
        feature('export', { kind: 'srt' });
    }

    function exportAudio() {
        if (!S.audio) { window.__toast(t('need_voice', 'Сначала озвучьте урок'), 'warning'); return; }
        save(window.LessonNarrate.toWav(S.audio.buffer), baseName() + '.wav');
        feature('export', { kind: 'wav' });
    }

    function exportVideo() {
        if (!S.audio) { window.__toast(t('need_voice', 'Сначала озвучьте урок'), 'warning'); return; }
        var res = parseInt(($('lm-res') || {}).value || '1080', 10);
        // Кадр СЛЕДУЕТ выбранным пропорциям, а не всегда 16:9: иначе слайд 4:3
        // уехал бы в широкий ролик с чёрными полями по бокам. Высота — из
        // выбора качества, ширина считается и округляется до ЧЁТНОГО:
        // нечётная сторона не принимается кодеком.
        var H = res === 720 ? 720 : 1080;
        var W = Math.round(H * window.LessonRender.W / window.LessonRender.H / 2) * 2;
        var bar = $('lm-progress');
        bar.hidden = false;
        setBusy(true, t('rendering', 'Собираем видеоурок…'));
        // Экран не должен гаснуть посреди рендера: на ноутбуке это выглядит
        // как зависший тул, а работа теряется.
        var lock = null;
        if (navigator.wakeLock && navigator.wakeLock.request) {
            navigator.wakeLock.request('screen').then(function (l) { lock = l; }).catch(function () {});
        }
        // Часы работы, а не стенные: вкладка, замороженная в фоне посреди
        // рендера, не рендерила — на Date.now() эта пауза раздувала «осталось ~»
        // до конца прогона (класс «≈30 ч на 89 %», /video-fps 26.09.2026).
        var workNow = function () { return (typeof window.__activeNow === 'function') ? window.__activeNow() : Date.now(); };
        var t0 = workNow();

        window.LessonExport.video({
            deck: S.deck, theme: theme(), images: imagesInOrder(),
            starts: S.audio.starts, durations: S.audio.durations,
            total: S.audio.total, audioBuffer: S.audio.buffer,
            brand: S.brand
        }, {
            width: W, height: H, fps: 25, format: 'mp4',
            captions: !!($('lm-burn-subs') || {}).checked,
            onProgress: function (p) {
                bar.value = Math.round(p * 100);
                var elapsed = (workNow() - t0) / 1000;
                var eta = p > 0.03 ? Math.max(0, elapsed / p - elapsed) : 0;
                $('lm-progress-label').textContent = Math.round(p * 100) + '%'
                    + (eta ? ' · ' + t('eta', 'осталось ~') + window.LessonExport.mmss(eta) : '');
            }
        }).then(function (blob) {
            save(blob, baseName() + '.mp4');
            feature('export', { kind: 'video', ms: Math.round(workNow() - t0), res: res });
            if (window.__aeToolComplete) window.__aeToolComplete({ kind: 'video', ms: Math.round(workNow() - t0) });
        }).catch(function (e) {
            window.__toast(t('video_failed', 'Видео собрать не удалось. Презентация и конспект уже готовы.'), 'error');
            if (window.__aeDiagnostic) window.__aeDiagnostic({ issue: 'video_failed', msg: String(e && e.message) });
        }).finally(function () {
            bar.hidden = true;
            setBusy(false);
            if (lock && lock.release) lock.release().catch(function () {});
        });
    }

    // ── навигация и служебное ────────────────────────────────────────────────
    function show(screen) {
        // Уходя с экрана урока, звук обязан замолкать: иначе диктор продолжает
        // читать поверх формы следующего урока.
        if (screen !== 'lesson' && player.on) pausePlayer(false);
        ['form', 'plan', 'lesson'].forEach(function (s) {
            var n = $('lm-screen-' + s);
            if (n) n.hidden = s !== screen;
        });
        var head = $('lm-screen-' + screen);
        // Только если экран уехал за пределы окна — страницу не дёргаем.
        if (head && head.scrollIntoView) head.scrollIntoView({ block: 'nearest' });
    }

    function setBusy(on, label) {
        S.busy = on;
        var b = $('lm-busy');
        b.hidden = !on;
        if (label) $('lm-busy-label').textContent = label;
        Array.prototype.forEach.call(document.querySelectorAll('.lm-act'), function (n) {
            n.disabled = on;
        });
        // Общее «разблокировать всё» не должно воскрешать кнопки, которым
        // нечего отдать: без звука видео, субтитры и WAV остаются закрытыми.
        if (!on) syncVoiceOnlyExports();
    }

    // ── старт ────────────────────────────────────────────────────────────────
    function init() {
        Array.prototype.forEach.call(document.querySelectorAll('input[name="lm-mode"]'), function (r) {
            r.addEventListener('change', function () { syncMode(); renderPrice(); });
        });
        fillLayoutPicker();
        if ($('lm-add-slide')) {
            $('lm-add-slide').addEventListener('click', function () {
                addSlide(($('lm-add-layout') || {}).value || 'bullets');
            });
        }
        Array.prototype.forEach.call(document.querySelectorAll('.lm-speed'), function (b) {
            b.addEventListener('click', function () { selectSpeed(b.dataset.speed); saveDraft(); loadVoicePrice(); });
        });

        // Длительность и класс меняют бюджет слов, а значит и цену голоса:
        // показанная сумма обязана двигаться вместе с настройками, иначе она
        // соврёт ровно в тот момент, когда человек её читает.
        ['lm-duration', 'lm-grade'].forEach(function (id) {
            var el = $(id);
            if (el) el.addEventListener('change', function () { loadVoicePrice(); });
        });
        Array.prototype.forEach.call(document.querySelectorAll('.lm-theme'), function (b) {
            b.addEventListener('click', function () {
                // Выбор руками сильнее подсказки по классу: дальше тему сам
                // тул не меняет, иначе она «прыгала» бы при смене класса.
                S.themePicked = true;
                applyTheme(b.dataset.theme, true);
                saveDraft();
            });
        });

        // Реквизиты школы учителя не меняются от урока к уроку — помним их.
        ['lm-cr-school', 'lm-cr-place', 'lm-cr-teacher'].forEach(function (id) {
            var el0 = $(id);
            if (!el0) return;
            el0.value = window.__toolSettings.load('lesson-maker', id, '') || '';
            el0.addEventListener('change', function () {
                window.__toolSettings.save('lesson-maker', id, el0.value.trim());
            });
        });
        // Если что-то уже заполнено — раскрываем блок, чтобы человек видел, что
        // именно уедет на титул.
        if ($('lm-credits') && ['lm-cr-school', 'lm-cr-place', 'lm-cr-teacher'].some(function (id) {
            return (($(id) || {}).value || '').trim() !== '';
        })) { $('lm-credits').open = true; }

        // Цель и задачи: три состояния, поля появляются только у «впишу сам».
        Array.prototype.forEach.call(document.querySelectorAll('.lm-goalmode'), function (btn) {
            btn.addEventListener('click', function () {
                Array.prototype.forEach.call(document.querySelectorAll('.lm-goalmode'), function (x) {
                    var on = x === btn;
                    x.classList.toggle('is-on', on);
                    x.setAttribute('aria-pressed', on ? 'true' : 'false');
                });
                var own = $('lm-goal-own');
                if (own) own.hidden = btn.dataset.goal !== 'own';
                feature('goal_mode', { v: btn.dataset.goal });
                saveDraft();
            });
        });

        // Появление по щелчку — привычка учителя, а не разовая настройка: кто
        // ведёт урок так, ведёт так всегда. Помним выбор между уроками.
        var revealBox = $('lm-reveal');
        if (revealBox) {
            revealBox.checked = window.__toolSettings.load('lesson-maker', 'reveal', '') === '1';
            revealBox.addEventListener('change', function () {
                window.__toolSettings.save('lesson-maker', 'reveal', revealBox.checked ? '1' : '');
            });
        }

        // Тема слайдов следует КЛАССУ, пока учитель не выбрал сам.
        // «Для детей до 7 класса нужны спокойные тона либо яркие, чтобы не
        // раздражать психику» — тёмная «школьная доска» осталась старшим.
        var grade = $('lm-grade');
        if (grade) {
            grade.addEventListener('change', function () {
                if (S.themePicked) return;
                var prof = (CFG.grade_map || {})[grade.value];
                var wanted = ((CFG.grades || {})[prof] || {}).theme;
                if (wanted && (CFG.themes || {})[wanted]) applyTheme(wanted, false);
            });
        }

        // Пропорции: восстанавливаем выбор, применяем ДО первой отрисовки.
        Array.prototype.forEach.call(document.querySelectorAll('.lm-aspect'), function (b) {
            b.addEventListener('click', function () {
                applyAspect(b.dataset.aspect, true);
                window.__toolSettings.save('lesson-maker', 'aspect', b.dataset.aspect);
                feature('aspect', { v: b.dataset.aspect });
            });
        });
        applyAspect(window.__toolSettings.load('lesson-maker', 'aspect', CFG.aspect_default || '16:9'), false);

        // Плеер: клик по кнопке — единственное место, где можно разбудить
        // AudioContext на iOS, поэтому вся логика старта живёт внутри жеста.
        var playBtn = $('lm-play');
        if (playBtn) {
            playBtn.addEventListener('click', function () {
                if (player.on) { pausePlayer(false); return; }
                feature('preview_play', { from: Math.round(player.offset) });
                playFrom(player.offset >= playerTotal() - 0.05 ? 0 : player.offset);
            });
        }
        var seek = $('lm-seek');
        if (seek) {
            seek.addEventListener('input', function () {
                var sec = playerTotal() * (parseInt(seek.value, 10) || 0) / 1000;
                if (player.on) { playFrom(sec); } else { player.offset = sec; drawPreview(slideAt(sec)); paintPlayer(); }
            });
        }

        // Ссылка на урок: первое нажатие включает показ и сохраняет, второе —
        // копирует. Одна кнопка вместо галочки и поля: делятся уроком в один
        // жест, а не настраивают его.
        var shareBtn = $('lm-share-btn');
        if (shareBtn) {
            shareBtn.addEventListener('click', function () {
                if (!saved.shared) {
                    feature('share_on', {});
                    saveDeck({ share: true }).then(function (j) {
                        if (j && j.url) copyLink(j.url);
                    });
                    return;
                }
                copyLink(saved.url);
            });
        }

        // Отозвать ссылку. Урок при этом остаётся у владельца — гасится только
        // доступ: страница по адресу начинает отдавать 404.
        var shareOff = $('lm-share-off');
        if (shareOff) {
            shareOff.addEventListener('click', function () {
                feature('share_off', {});
                saveDeck({ share: false }).then(function (j) {
                    if (j) window.__toast(t('share_off_done', 'Ссылка выключена — страница больше не открывается'), 'success');
                });
            });
        }

        // Показ реквизитов по ссылке — отдельное решение, отдельная галочка.
        var shareCreds = $('lm-share-creds');
        if (shareCreds) {
            shareCreds.addEventListener('change', function () {
                var on = shareCreds.checked;
                feature('share_credits', { on: on });
                saveDeck({ share_credits: on }).then(function (j) {
                    if (!j) { shareCreds.checked = !on; renderCredVis(); return; }
                    window.__toast(on
                        ? t('share_creds_on', 'Школа и ФИО теперь видны тем, у кого есть ссылка')
                        : t('share_creds_off', 'Школа и ФИО по ссылке скрыты'), on ? 'warning' : 'success');
                });
            });
        }

        $('lm-build').addEventListener('click', buildPlan);
        $('lm-to-lesson').addEventListener('click', function () { buildLesson(); });
        $('lm-to-lesson-silent').addEventListener('click', buildSilent);
        $('lm-back-form').addEventListener('click', function () { show('form'); });
        $('lm-back-plan').addEventListener('click', function () {
            // Возврат к плану — обычный путь правки. Ниже, на кнопке сборки,
            // пересчитается только изменённое.
            renderPlan();
            show('plan');
        });
        $('lm-exp-pptx').addEventListener('click', exportPptx);
        $('lm-exp-pdf').addEventListener('click', exportPdf);
        $('lm-exp-doc').addEventListener('click', exportDoc);
        if ($('lm-exp-map')) $('lm-exp-map').addEventListener('click', exportMap);
        $('lm-exp-srt').addEventListener('click', exportSrt);
        $('lm-exp-wav').addEventListener('click', exportAudio);
        $('lm-exp-video').addEventListener('click', exportVideo);

        // Аккордеон озвучки: настройки прячем от тех, кому голос не нужен,
        // а цену показываем ДО нажатия — перк «Премиуму бесплатно» не продаёт,
        // если о нём узнают уже после списания.
        var wantVoice = $('lm-want-voice');
        if (wantVoice) {
            var syncVoiceUi = function () {
                $('lm-voice-panel').hidden = !wantVoice.checked;
                var main = $('lm-to-lesson'), alt = $('lm-to-lesson-silent');
                if (main) main.textContent = wantVoice.checked
                    ? t('do_voice', 'Озвучить и собрать')
                    : t('do_build_lesson', 'Собрать урок');
                // Запасная кнопка нужна только когда основная платная.
                if (alt) alt.hidden = !wantVoice.checked;
                renderPrice();          // итог зависит от галочки — перерисовать сразу
                saveDraft();
            };
            wantVoice.addEventListener('change', syncVoiceUi);
            syncVoiceUi();
        }

        // Смета нужна ВСЕГДА, а не только при заказанном голосе: урок сам по
        // себе платный, и узнать об этом после нажатия — худший момент.
        loadVoicePrice();

        // Свои слайды из PDF: выбор файла сразу и запускает разбор — отдельная
        // кнопка «поехали» здесь была бы лишним шагом.
        var pf = $('lm-pdf-file');
        if (pf) {
            pf.addEventListener('change', function () {
                var f = pf.files && pf.files[0];
                if (f) importPdf(f);
                pf.value = '';       // тот же файл можно выбрать повторно
            });
        }

        // Правка звука: пересборка микса, без обращения к синтезу.
        if ($('lm-lead-in')) {
            $('lm-lead-in').addEventListener('change', function () {
                edit.leadIn = Math.max(0, parseFloat(this.value) || 0);
                feature('sound_edit', { what: 'lead_in', v: edit.leadIn });
                rebuildAudio();
            });
        }
        if ($('lm-pause-len')) {
            var applyPause = function () {
                var i = parseInt(($('lm-pause-slide') || {}).value || '0', 10);
                var v = Math.max(0, parseFloat(($('lm-pause-len') || {}).value) || 0);
                if (v > 0) edit.pauses[i] = v; else delete edit.pauses[i];
                feature('sound_edit', { what: 'pause', i: i, v: v });
                rebuildAudio();
            };
            $('lm-pause-len').addEventListener('change', applyPause);
        }
        if ($('lm-pause-slide')) {
            // При выборе другого слайда показываем ЕГО паузу, а не чужую:
            // иначе поле врёт о том, что уже настроено.
            $('lm-pause-slide').addEventListener('change', function () {
                var i = parseInt(this.value || '0', 10);
                if ($('lm-pause-len')) $('lm-pause-len').value = String(edit.pauses[i] || 0);
            });
        }
        var mf = $('lm-music-file');
        if (mf) {
            mf.addEventListener('change', function () {
                var f = mf.files && mf.files[0];
                if (!f) return;
                setBusy(true, t('sound_music_load', 'Читаем мелодию…'));
                window.LessonNarrate.decodeFile(f)
                    .then(function (buf) {
                        edit.music = { buffer: buf, gain: (parseInt(($('lm-music-vol') || {}).value || '12', 10)) / 100 };
                        ['lm-music-vol', 'lm-music-clear'].forEach(function (id) { if ($(id)) $(id).hidden = false; });
                        feature('sound_edit', { what: 'music' });
                        rebuildAudio();
                    })
                    .catch(function () { window.__toast(t('sound_music_failed', 'Не удалось прочитать мелодию'), 'error'); })
                    .finally(function () { setBusy(false); });
            });
        }
        if ($('lm-music-vol')) {
            $('lm-music-vol').addEventListener('change', function () {
                if (!edit.music) return;
                edit.music.gain = (parseInt(this.value, 10) || 12) / 100;
                rebuildAudio();
            });
        }
        if ($('lm-music-clear')) {
            $('lm-music-clear').addEventListener('click', function () {
                edit.music = null;
                ['lm-music-vol', 'lm-music-clear'].forEach(function (id) { if ($(id)) $(id).hidden = true; });
                rebuildAudio();
            });
        }

        // Знак школы: выбор файла, угол, размер, круглая маска, сброс.
        var bf = $('lm-brand-file');
        if (bf) {
            bf.addEventListener('change', function () {
                var f = bf.files && bf.files[0];
                if (!f) return;
                brandFromFile(f)
                    .then(function (u) { return applyBrand(u); })
                    .then(function () {
                        feature('brand_on', {});
                        window.__toolSettings.save('lesson-maker', 'brand', S.brand && S.brand.url);
                    })
                    .catch(function () { window.__toast(t('brand_failed', 'Не удалось прочитать изображение'), 'error'); });
            });
        }
        ['lm-brand-corner', 'lm-brand-size', 'lm-brand-round'].forEach(function (id) {
            var el0 = $(id);
            if (el0) el0.addEventListener('change', function () { if (S.brand) applyBrand(S.brand.url); });
        });
        if ($('lm-brand-size')) $('lm-brand-size').addEventListener('input', function () { if (S.brand) applyBrand(S.brand.url); });
        if ($('lm-brand-clear')) {
            $('lm-brand-clear').addEventListener('click', function () {
                applyBrand(null);
                window.__toolSettings.save('lesson-maker', 'brand', '');
            });
        }
        // Логотип школы у учителя один и тот же от урока к уроку — помним его.
        var savedBrand = window.__toolSettings.load('lesson-maker', 'brand', '');
        if (savedBrand && String(savedBrand).indexOf('data:image') === 0) applyBrand(savedBrand);

        // Образец урока: грузим содержимое на первом раскрытии, не раньше.
        var sample = $('lm-sample');
        if (sample) {
            sample.addEventListener('toggle', function () {
                if (sample.open) loadSample(sample);
            });
        }

        // Открыть сохранённый урок: ссылка «Открыть урок» из «Моих генераций»
        // приходит с ?deck=<токен>. Без этого кнопка в библиотеке вела бы на
        // пустую форму — обещание, которого страница не выполняет.
        var wanted = new URLSearchParams(location.search).get('deck');
        if (wanted) openSaved(wanted);

        syncMode();
        selectSpeed('normal');

        // Умеет ли ЭТОТ браузер собрать видео — выясняем заранее, а не после
        // того, как учитель прождал озвучку и нажал «Видеоурок». Проверка
        // спрашивает сам примитив (WebCodecs + муксер), а не гадает по UA.
        // ⚠️ Проверять надо ИМЕННО так: headless-WebKit на стенде не имеет ни
        // VideoEncoder, ни MediaRecorder, тогда как настоящий Safari 16.4+
        // WebCodecs умеет — вывод «Safari не может» из прогона на стенде был
        // бы ложным.
        if (window.CanvasVideoExport && window.CanvasVideoExport.isSupported) {
            window.CanvasVideoExport.isSupported({ width: 1920, height: 1080, fps: 25, format: 'mp4' })
                .then(function (r) {
                    if (r && r.ok) return;
                    var btn = $('lm-exp-video');
                    if (!btn) return;
                    btn.disabled = true;
                    btn.dataset.unsupported = '1';
                    btn.classList.add('is-off');
                    var note = btn.querySelector('span');
                    if (note) note.textContent = t('video_unsupported', 'Этот браузер не умеет собирать видео — остальные файлы доступны');
                })
                .catch(function () { /* не смогли выяснить — оставляем кнопку */ });
        }

        // Незаконченный заказ урока (обрыв, перезагрузка посреди сборки):
        // урок, возможно, уже готов и оплачен — забираем его, а не предлагаем
        // старый черновик и не ждём второго нажатия.
        var pend = pendingOrder();
        if (pend) {
            feature('plan_resume', { age_s: Math.round((Date.now() - pend.at) / 1000) });
            waitForOrder(pend.rid, null, true);
        }

        var d = pend ? null : loadDraft();
        if (d && d.deck && (d.slides !== 0)) {
            var bar = $('lm-restore');
            bar.hidden = false;
            $('lm-restore-yes').addEventListener('click', function () {
                freshDeck();
                S.deck = adoptDeck(d.deck);
                if (d.token) { saved.token = d.token; renderShare(); }
                S.themeKey = d.themeKey || 'board';
                applyForm(d.form);
                bar.hidden = true;
                restoreOwnImages().then(function () {
                    renderPlan();
                    show('plan');
                    fetchImages();
                    feature('draft_restored', { own: Object.keys(ownFiles).length });
                });
            });
            $('lm-restore-no').addEventListener('click', function () {
                dropDraft(); dropOwnImages(); bar.hidden = true;
            });
        }

        stage('ready');
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
